import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AboutEntityModel } from '@lib/about/models';
import { OfficerActivityIncidentModel } from '@lib/activity-tracking/models';
import { AgencyService } from '@lib/agency/data-access';
import { AssociationCountModel, ChargingDocumentIncidentReportModel, ChargingIncidentModel, CleryArrestModel, CleryLocationModel, CleryPersonModel, CleryPrimaryInfoModel, IncidentArrestUcrDetailModel, IncidentIncidentModel, IncidentLeokaDetailModel, IncidentLocationModel, IncidentReportAndSupportingDepositionModel, IncidentUcrDetailModel, NibrsOffenderModel, NibrsPrimaryInformationModel, NibrsPropertyModel, NibrsVictimModel, OrganizationIncidentModel, PersonIncidentModel, PropertyIncidentModel, TrafficStopPrimaryInfoModel, UcrHomicideMasterModel, UcrPersonModel, UcrPropertyModel, VehicleIncidentModel, VehiclePersonModel } from '@lib/association/models';
import { AuthService } from '@lib/auth/data-access';
import { CLAIMS } from '@lib/auth/models';
import { NibrsArresteeDetailModel } from '@lib/charging/models';
import { cleryArrestPopoutFormFields, cleryLocationPopoutFormFields, cleryPersonPopoutFormFields, cleryPrimaryInfoFormFields, incidentAssignmentsFields, incidentChargingFields, incidentDatesFields, incidentModusOperandiFields, incidentOrganizationFields, incidentPersonFields, incidentPrimaryInformationFields, incidentProfessionalStandards, incidentPropertyFields, incidentReviewFields, incidentReviewersFields, incidentTagsFields, incidentUnitOfficerFields, nibrsArresteePopoutFormFields, nibrsOffenderPopoutFormFields, nibrsOffensePopoutFormFields, nibrsPrimaryInformationFields, nibrsPropertyPopoutFormFields, nibrsVictimPopoutFormFields, supervisorFields, trafficStopPersonPopoutFormFields, trafficStopPrimaryInfoFormFields, ucrArresteePopoutFormFields, ucrHomicideFields, ucrLeokaFields, ucrPersonFields, ucrPropertyPopoutFormFields } from '@lib/configurable-field/models';
import { FeatureToggleService } from '@lib/feature-toggle/data-access';
import { FeatureToggleType } from '@lib/feature-toggle/models';
import { CadEventHistoryModel, IncidentAssignmentModel, IncidentAssignmentsFilterModel, IncidentDatesModel, IncidentModel, IncidentNarrativeDraftFilterModel, IncidentNarrativeModel, IncidentNarrativeSectionHistoryModel, IncidentAssociateToVehiclesSectionHistoryModel, IncidentPrimaryInformationModel, IncidentReportAccessModel, IncidentReportAccessUpdateModel, IncidentReportAddressHistoryModel, IncidentReportAndIncidentReportHistoryModel, IncidentReportApproverGroupModel, IncidentReportCommissionAccreditationLawEnforcementAgencyDto, IncidentReportDateHistoryModel, IncidentReportFilterModel, IncidentReportLawEnforcementAccreditationDto, IncidentReportPrimaryInfoSectionHistoryModel, IncidentReviewApproverModel, IncidentReviewCommentModel, IncidentReviewFilterModel, IncidentReviewModel, IncidentReviewStatus, IncidentStatutoryViolationHistoryModel, IncidentTagModel, IncidentTypeModel, NarrativeAccessModel, NarrativeContentFilterModel, NarrativeModel, OrganizationAndIncidentReportHistoryModel, PersonAndIncidentReportHistoryModel, PropertyAndIncidentReportHistoryModel, UnitIncidentModel, UnitOfficerHistoryModel, UnitOfficerModel, UnitOfficerSummaryModel, UnsubmittedFilterModel, VehicleDetailAndIncidentReportHistoryModel, EventRecordType, IncidentEventRecordType } from '@lib/incident/models';
import { IncidentModusOperandiModel } from '@lib/modus-operandi/models';
import { ReportNumberGenerationService } from '@lib/report-number-generation/data-access';
import { ReportNumberGenerationType } from '@lib/report-number-generation/models';
import { CodeService, EnvironmentService, ToastMessageService } from '@lib/shared/data-access';
import { CodeModel, CodeType, CountType, FormMode, UCR_HOMICIDE_CODES } from '@lib/shared/models';
import { FormUtility } from '@lib/shared/util';
import { EMPTY, Observable, Subject, catchError, forkJoin, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { AlarmResponseModel } from '@lib/alarm/models'
import { AgencyStateConfigurationModel } from '@lib/system-configuration/models';
import { PersonService } from '@lib/person/data-access';
import { EmployeeModel } from '@lib/person/models';
import { IncidentUcrLeokaInvolvementType } from '@lib/incident/models';

@Injectable({
    providedIn: 'root'
})
export class IncidentService extends FormUtility {

    private refreshSourceSignal = signal(false);
    public refreshComputedSignal = computed(() => { return this.refreshSourceSignal() });

    private incidentIdSourceSignal = signal(0);
    public incidentIdComputedSignal = computed(() => { return this.incidentIdSourceSignal() });

    private wizardModeSourceSignal = signal(true)
    public wizardModeComputedSignal = computed(() => { return this.wizardModeSourceSignal() });

    private formModeSourceSignal = signal(FormMode.Read)
    public formModeComputedSignal = computed(() => { return this.formModeSourceSignal() });

    private incidentPrimaryInformationSourceSignal = signal<IncidentPrimaryInformationModel | undefined>(undefined);
    public incidentPrimaryInformationComputedSignal = computed(() => { return this.incidentPrimaryInformationSourceSignal() });

    private reviewModeSourceSignal = signal(false)
    public reviewModeComputedSignal = computed(() => { return this.reviewModeSourceSignal() });

    private incidentReportTagsSourceSignal = signal([] as number[]);
    public incidentReportTagsComputedSignal = computed(() => { return this.incidentReportTagsSourceSignal() });

    private incidentReportTypeTagsSourceSignal = signal<CodeModel[]>([]);
    public incidentReportTypeTagsComputedSignal = computed(() => { return this.incidentReportTypeTagsSourceSignal() });

    private incidentReportTypesSourceSignal = signal([] as IncidentTypeModel[])
    public incidentReportTypesComputedSignal = computed(() => { return this.incidentReportTypesSourceSignal() });

    // Event record types (Incident / Call For Service) currently selected for this incident.
    // Exposed module-wide so other sections/sidebar can show/hide based on the selection.
    //
    // SEEDED WITH INCIDENT REPORT, not empty. Legacy no longer shows the Event Record Type section, so nothing
    // populates this until the (hidden) type component mounts — and that only happens on the Primary Information
    // step. Starting empty meant refreshing straight into any other section left every `length > 0` gate false:
    // the whole side menu and the full-view accordion rendered blank.
    //
    // Hardcoding Incident Report is the interim product decision ("for now"), so the default IS the answer rather
    // than a placeholder waiting to be corrected. The type component still overwrites this with the record's real
    // types when it mounts.
    private eventRecordTypesSourceSignal = signal<EventRecordType[]>([EventRecordType.Incident]);
    public eventRecordTypesComputedSignal = computed(() => { return this.eventRecordTypesSourceSignal() });

    public isEventRecordIncidentComputedSignal = computed(() => this.eventRecordTypesSourceSignal().includes(EventRecordType.Incident));
    public isEventRecordCallForServiceComputedSignal = computed(() => this.eventRecordTypesSourceSignal().includes(EventRecordType.CallForService));


    //#region NIBRS
    private isNibrsIncidentSourceSignal = signal(false);
    public isNibrsIncidentComputedSignal = computed(() => { return !!this.incidentIdSourceSignal() && this.isNibrsIncidentSourceSignal() });
    public canViewNibrsIncidentComputedSignal = computed(() => {
        const nibrsFeatureEnabled = this.featureToggleService.tenantFeaturesComputedSignal()
            .some(f => f.id == FeatureToggleType.NIBRS && f.isEnabled);
        return nibrsFeatureEnabled && this.isNibrsIncidentComputedSignal();
    });
    private showNibrsValidationSourceSignal = signal(true);
    public showNibrsValidationComputedSignal = computed(() => {
        return this.showNibrsValidationSourceSignal() && this.canViewNibrsIncidentComputedSignal()
            && this.authService.hasAnyClaim([CLAIMS.NIBRS_VALIDATION_SUBMISSION_VALIDATE, CLAIMS.NIBRS_VALIDATION_SUBMISSION_VALIDATE_SUBMIT, CLAIMS.NIBRS_VALIDATION_SUBMISSION_VALIDATE_SUBMIT_DELETE]);
    });
    private incidentNibrsPrimaryInformationSourceSignal = signal<NibrsPrimaryInformationModel | null>(null);
    public incidentNibrsPrimaryInformationComputedSignal = computed(() => this.incidentNibrsPrimaryInformationSourceSignal());
    public incidentNibrsNumberComputedSignal = computed(() => {
        const incidentIdentifiers = this.incidentPrimaryInformationComputedSignal()?.incidentReportIdentifiers;
        if (!incidentIdentifiers || incidentIdentifiers.length === 0) return 'Unknown';
        return incidentIdentifiers.find(i => i.incidentReportIdentifierType?.code === 'NB')?.identifier || 'Unknown';
    })
    //#endregion

    //#region UCR
    private isUcrIncidentSourceSignal = signal(false);
    public isUcrIncidentComputedSignal = computed(() => { return !!this.incidentIdSourceSignal() && this.isUcrIncidentSourceSignal() });
    public canViewUcrIncidentComputedSignal = computed(() => {
        const ucrFeatureEnabled = this.featureToggleService.tenantFeaturesComputedSignal()
            .some(f => f.id === FeatureToggleType.UCR && f.isEnabled);
        return ucrFeatureEnabled && this.isUcrIncidentComputedSignal()
            && this.authService.hasAnyClaim([CLAIMS.UCR_DATA_READ, CLAIMS.UCR_DATA_READ_WRITE, CLAIMS.UCR_DATA_READ_WRITE_DELETE]);
    });
    private showUcrValidationSourceSignal = signal(true);
    public showUcrValidationComputedSignal = computed(() => {
        return this.showUcrValidationSourceSignal() && this.canViewUcrIncidentComputedSignal()
            && this.authService.hasAnyClaim([CLAIMS.UCR_SUBMIT_READ_WRITE, CLAIMS.UCR_SUBMIT_READ_WRITE_DELETE]);
    })

    private incidentUcrPersonsSourceSignal = signal<UcrPersonModel[]>([]);
    public incidentUcrPersonsComputedSignal = computed(() => this.incidentUcrPersonsSourceSignal());
    private incidentUcrMainInformationSourceSignal = signal<IncidentUcrDetailModel | null>(null);
    public incidentUcrMainInformationComputedSignal = computed(() => this.incidentUcrMainInformationSourceSignal());
    private incidentUcrHomicideInformationSourceSignal = signal<UcrHomicideMasterModel | null>(null);
    public incidentUcrHomicideInformationComputedSignal = computed(() => this.incidentUcrHomicideInformationSourceSignal());
    private incidentUcrLeokaDetailModelSourceSignal = signal<IncidentLeokaDetailModel | null>(null);
    public incidentUcrLeokaDetailModelComputedSignal = computed(() => this.incidentUcrLeokaDetailModelSourceSignal());

    public canViewUcrLeokaComputed = computed(() => {
        const ucrPersons = this.incidentUcrPersonsComputedSignal();

        return ucrPersons?.some(person =>
            person.ucrInvolvementCode?.id !== IncidentUcrLeokaInvolvementType.Victim
        ) ?? false;
    });

    public canViewUcrHomicideComputed = computed(() => {
        const ucrMainInfo = this.incidentUcrMainInformationComputedSignal();

        return UCR_HOMICIDE_CODES.includes(ucrMainInfo?.ucrCode?.code ?? '');
    });

    public incidentUcrLeokaPersonsComputed = computed(() => {
        const ucrPersons = this.incidentUcrPersonsComputedSignal();

        return ucrPersons?.filter(person =>
            person.ucrInvolvementCode?.id !== IncidentUcrLeokaInvolvementType.Victim
        ) ?? [];
    });
    //#endregion

    //#region Clery
    private isCleryIncidentSourceSignal = signal(false);
    public isCleryIncidentComputedSignal = computed(() => { return !!this.incidentIdSourceSignal() && this.isCleryIncidentSourceSignal() });
    public canViewCleryIncidentComputedSignal = computed(() => {
        const cleryFeatureEnabled = this.featureToggleService.tenantFeaturesComputedSignal()
            .some(f => f.id === FeatureToggleType.Clery && f.isEnabled);
        return cleryFeatureEnabled && this.isCleryIncidentComputedSignal();
        //TODO: once claims are implemented, add && this.authService.hasAnyClaim([list o' claims goes here])
    })
    //#endregion

    //#region Traffic Stop
    private isTrafficStopIncidentSourceSignal = signal(false);
    public isTrafficStopIncidentComputedSignal = computed(() => { return !!this.incidentIdSourceSignal() && this.isTrafficStopIncidentSourceSignal() });
    public canViewTrafficStopIncidentComputedSignal = computed(() => {
        const trafficStopFeatureEnabled = this.featureToggleService.tenantFeaturesComputedSignal()
            .some(f => f.id === FeatureToggleType.TrafficStop && f.isEnabled);
        return trafficStopFeatureEnabled && this.isTrafficStopIncidentComputedSignal() && this.hasConfiguredState(['PA']);
    })
    //#endregion

    private agencyStateConfigurationSourceSignal = signal<AgencyStateConfigurationModel | null>(null);
    public agencyStateConfigurationComputedSignal = computed(() => this.agencyStateConfigurationSourceSignal());

    private incidentLoadedSectionsSourceSignal = signal<{ [key: string]: boolean }>({});
    public incidentLoadedSectionsComputedSignal = computed(() => this.incidentLoadedSectionsSourceSignal());

    // associations

    private incidentPersonsSourceSignal = signal<PersonIncidentModel[]>([]);
    public incidentPersonsComputedSignal = computed(() => { return this.incidentPersonsSourceSignal() });

    private incidentChargingsSourceSignal = signal<ChargingIncidentModel[]>([]);
    public incidentChargingsComputedSignal = computed(() => { return this.incidentChargingsSourceSignal() });

    private incidentVehiclesSourceSignal = signal<VehicleIncidentModel[]>([]);
    public incidentVehiclesComputedSignal = computed(() => { return this.incidentVehiclesSourceSignal() });

    private incidentVehiclesPersonsSourceSignal = signal<VehiclePersonModel[]>([]);
    public incidentVehiclesPersonsComputedSignal = computed(() => { return this.incidentVehiclesPersonsSourceSignal() });

    private incidentLocationsSourceSignal = signal<IncidentLocationModel[]>([]);
    public incidentLocationsComputedSignal = computed(() => { return this.incidentLocationsSourceSignal() });

    private incidentIncidentsSourceSignal = signal<IncidentIncidentModel[]>([]);
    public incidentIncidentsComputedSignal = computed(() => { return this.incidentIncidentsSourceSignal() });

    private incidentUnitsAndOfficersSourceSignal = signal<UnitOfficerSummaryModel[]>([]);
    public incidentUnitsAndOfficersComputedSignal = computed(() => { return this.incidentUnitsAndOfficersSourceSignal() });

    private incidentOrganizationsSourceSignal = signal<OrganizationIncidentModel[]>([]);
    public incidentOrganizationsComputedSignal = computed(() => { return this.incidentOrganizationsSourceSignal() });

    private incidentActivitiesSourceSignal = signal<OfficerActivityIncidentModel[]>([]);
    public incidentActivitiesComputedSignal = computed<OfficerActivityIncidentModel[]>(() => { return this.incidentActivitiesSourceSignal(); });

    private incidentPropertiesSourceSignal = signal<PropertyIncidentModel[]>([]);
    public incidentPropertiesComputedSignal = computed<PropertyIncidentModel[]>(() => { return this.incidentPropertiesSourceSignal(); });

    private incidentNibrsVictimsSourceSignal = signal<NibrsVictimModel[]>([]);
    public incidentNibrsVictimsComputedSignal = computed<NibrsVictimModel[]>(() => this.incidentNibrsVictimsSourceSignal());
    private incidentActiveNibrsVictimSourceSignal = signal<NibrsVictimModel | null>(null);
    public incidentActiveNibrsVictimComputedSignal = computed(() => this.incidentActiveNibrsVictimSourceSignal());

    private incidentNibrsOffendersSourceSignal = signal<NibrsOffenderModel[]>([]);
    public incidentNibrsOffendersComputedSignal = computed<NibrsOffenderModel[]>(() => this.incidentNibrsOffendersSourceSignal());
    private incidentActiveNibrsOffenderSourceSignal = signal<NibrsOffenderModel | null>(null);
    public incidentActiveNibrsOffenderComputedSignal = computed(() => this.incidentActiveNibrsOffenderSourceSignal());

    private incidentNibrsPropertiesSourceSignal = signal<NibrsPropertyModel[]>([]);
    public incidentNibrsPropertiesComputedSignal = computed<NibrsPropertyModel[]>(() => this.incidentNibrsPropertiesSourceSignal());
    private incidentActiveNibrsPropertySourceSignal = signal<NibrsPropertyModel | null>(null);
    public incidentActiveNibrsPropertyComputedSignal = computed(() => this.incidentActiveNibrsPropertySourceSignal());

    private incidentNibrsArresteesSourceSignal = signal<ChargingIncidentModel[]>([]);
    public incidentNibrsArresteesComputedSignal = computed<ChargingIncidentModel[]>(() => this.incidentNibrsArresteesSourceSignal());
    private incidentActiveNibrsArresteeDetailSourceSignal = signal<NibrsArresteeDetailModel | null>(null);
    public incidentActiveNibrsArresteeDetailComputedSignal = computed(() => this.incidentActiveNibrsArresteeDetailSourceSignal());

    private incidentUcrPropertiesSourceSignal = signal<UcrPropertyModel[]>([]);
    public incidentUcrPropertiesComputedSignal = computed<UcrPropertyModel[]>(() => this.incidentUcrPropertiesSourceSignal());
    private incidentActiveUcrPropertySourceSignal = signal<UcrPropertyModel | null>(null);
    public incidentActiveUcrPropertyComputedSignal = computed(() => this.incidentActiveUcrPropertySourceSignal());

    private incidentUcrArresteesSourceSignal = signal<IncidentArrestUcrDetailModel[]>([]);
    public incidentUcrArresteesComputedSignal = computed<IncidentArrestUcrDetailModel[]>(() => this.incidentUcrArresteesSourceSignal());
    private incidentActiveUcrArresteeSourceSignal = signal<IncidentArrestUcrDetailModel | null>(null);
    public incidentActiveUcrArresteeComputedSignal = computed(() => this.incidentActiveUcrArresteeSourceSignal());

    private incidentCleryPersonsSourceSignal = signal<CleryPersonModel[]>([]);
    public incidentCleryPersonsComputedSignal = computed(() => this.incidentCleryPersonsSourceSignal());
    private incidentActiveCleryPersonSourceSignal = signal<CleryPersonModel | null>(null);
    public incidentActiveCleryPersonComputedSignal = computed(() => this.incidentActiveCleryPersonSourceSignal());

    private incidentCleryLocationsSourceSignal = signal<CleryLocationModel[]>([]);
    public incidentCleryLocationsComputedSignal = computed(() => this.incidentCleryLocationsSourceSignal());
    private incidentActiveCleryLocationSourceSignal = signal<CleryLocationModel | null>(null);
    public incidentActiveCleryLocationComputedSignal = computed(() => this.incidentActiveCleryLocationSourceSignal());

    private incidentCleryArrestsSourceSignal = signal<CleryArrestModel[]>([]);
    public incidentCleryArrestsComputedSignal = computed(() => this.incidentCleryArrestsSourceSignal());
    private incidentActiveCleryArrestSourceSignal = signal<CleryArrestModel | null>(null);
    public incidentActiveCleryArrestComputedSignal = computed(() => this.incidentActiveCleryArrestSourceSignal());

    private incidentTrafficStopPersonsSourceSignal = signal<PersonIncidentModel[]>([]);
    public incidentTrafficStopPersonsComputedSignal = computed(() => this.incidentTrafficStopPersonsSourceSignal());
    private incidentActiveTrafficStopPersonSourceSignal = signal<number | null>(null);
    public incidentActiveTrafficStopPersonComputedSignal = computed(() => this.incidentActiveTrafficStopPersonSourceSignal());

    // filtered incidents
    private filteredIncidentReportSourceSignal = signal<IncidentModel[]>([]);
    public filteredIncidentReportComputedSignal = computed(() => { return this.filteredIncidentReportSourceSignal() });

    // unsubmitted incidents
    private unsubmittedIncidentReportsSourceSignal = signal<IncidentModel[]>([]);
    public unsubmittedIncidentReportsComputedSignal = computed(() => { return this.unsubmittedIncidentReportsSourceSignal() });

    // assignments
    private incidentReportAssignmentsSourceSignal = signal<IncidentAssignmentModel[]>([]);
    public incidentReportAssignmentsComputedSignal = computed(() => { return this.incidentReportAssignmentsSourceSignal() });

    private filteredAssignmentsSourceSignal = signal<IncidentAssignmentModel[]>([]);
    public filteredAssignmentsComputedSignal = computed(() => { return this.filteredAssignmentsSourceSignal() });

    // Triggering a filter call from another component so need to set the loading here for main table
    private assignmentsLoadingSignal = signal<boolean>(false);
    public assignmentsLoadingComputedSignal = computed(() => { return this.assignmentsLoadingSignal() });

    private scrollToAssignmentSourceSignal = signal<string | undefined>(undefined);
    public scrollToAssignmentComputedSignal = computed(() => { return this.scrollToAssignmentSourceSignal() });

    // review
    private incidentReportReviewSourceSignal = signal<IncidentReviewModel | null>(null);
    public incidentReportReviewComputedSignal = computed(() => { return this.incidentReportReviewSourceSignal() });
    public incidentReportReview$ = toObservable(this.incidentReportReviewComputedSignal);

    private incidentReportReviewsSourceSignal = signal<IncidentReviewModel[]>([]);
    public incidentReportReviewsComputedSignal = computed(() => { return this.incidentReportReviewsSourceSignal() });

    private incidentReportReviewsFilteredCountSourceSignal = signal<number>(0);
    public incidentReportReviewsFilteredCountComputedSignal = computed(() => { return this.incidentReportReviewsFilteredCountSourceSignal() });

    private incidentReportNarrativeReviewsSourceSignal = signal<IncidentReviewModel[]>([]);
    public incidentReportNarrativeReviewsComputedSignal = computed(() => { return this.incidentReportNarrativeReviewsSourceSignal() });

    private isComingFromOutsideModuleSourceSignal = signal(false)
    public isComingFromOutsideModuleComputedSignal = computed(() => { return this.isComingFromOutsideModuleSourceSignal() });

    private navigateDirectlyToRouteSourceSignal = signal('')
    public navigateDirectlyToRouteComputedSignal = computed(() => { return this.navigateDirectlyToRouteSourceSignal() });

    // Triggering a filter call from another component so need to set the loading here for main table
    private incidentReportReviewsLoadingSignal = signal<boolean>(false);
    public incidentReportReviewsLoadingComputedSignal = computed(() => { return this.incidentReportReviewsLoadingSignal() });

    //Narratives
    private incidentReportNarrativesSourceSignal = signal<NarrativeModel[]>([]);
    public incidentReportNarrativesComputedSignal = computed(() => { return this.incidentReportNarrativesSourceSignal() });
    private incidentReportActiveNarrativeSignal = signal<NarrativeModel | null>(null);
    public incidentReportActiveNarrativeComputedSignal = computed(() => { return this.incidentReportActiveNarrativeSignal() });

    private incidentReportSupportingDepositionSourceSignal = signal<IncidentReportAndSupportingDepositionModel[]>([]);
    public incidentReportSupportingDepositionComputedSignal = computed(() => { return this.incidentReportSupportingDepositionSourceSignal() });

    private assocationCountsSourceSignal = signal<AssociationCountModel[]>([]);
    public assocationCountsComputedSignal = computed<AssociationCountModel[]>(() => { return this.assocationCountsSourceSignal(); });

    private narrativeDraftsSourceSignal = signal<IncidentNarrativeModel[]>([]);
    public narrativeDraftsComputedSignal = computed(() => { return this.narrativeDraftsSourceSignal(); });

    private unsubmittedNarrativesSourceSignal = signal<IncidentNarrativeModel[]>([]);
    public unsubmittedNarrativesComputedSignal = computed(() => { return this.unsubmittedNarrativesSourceSignal(); });

    private chargingDocumentsSourceSignal = signal<ChargingDocumentIncidentReportModel[]>([]);
    public chargingDocumentsComputedSignal = computed<ChargingDocumentIncidentReportModel[]>(() => {
        return this.chargingDocumentsSourceSignal();
    });

    private alarmResponsesSourceSignal = signal<AlarmResponseModel[]>([]);
    public alarmResponsesComputedSignal = computed(() => { return this.alarmResponsesSourceSignal(); });

    private isNewYorkAgencyStateSourceSignal = signal<boolean | undefined>(undefined);
    public isNewYorkAgencyStateComputedSignal = computed(() => { return this.isNewYorkAgencyStateSourceSignal(); });

    public setIsNewYorkAgencyState(isNY: boolean) {
        this.isNewYorkAgencyStateSourceSignal.set(isNY);
    }

    public updateEvent = new Subject<any>();

    public authService = inject(AuthService);
    private personService = inject(PersonService);
    private httpClient = inject(HttpClient);
    private environmentService = inject(EnvironmentService);
    private agencyService = inject(AgencyService);
    private toastMessageService = inject(ToastMessageService);
    private codeService = inject(CodeService);
    private featureToggleService = inject(FeatureToggleService);
    private reportNumberGenerationService = inject(ReportNumberGenerationService);

    editFormMode(formMode: FormMode) {
        this.formModeSourceSignal.set(formMode);
    }

    editWizardMode(wizardMode: boolean) {
        this.wizardModeSourceSignal.set(wizardMode);
    }

    public editReviewMode(reviewMode: boolean) {
        this.reviewModeSourceSignal.set(reviewMode);
    }

    public editIncidentId(incidentId: number) {
        // for better loading experience, clear out the review
        this.incidentReportReviewSourceSignal.set({
            id: undefined,
            incidentReportId: 0,
            reviewEmployeeId: 0,
            approvers: [],
            approverGroups: [],
            incidentReportReviewComments: []
        })
        this.incidentIdSourceSignal.set(incidentId);

        //reset flag to get the validation to appear when a new incident loads
        this.showNibrsValidationSourceSignal.set(true);
        this.showUcrValidationSourceSignal.set(true);

        //clear out the NIBRS data from last incident to ensure fresh reload happens
        this.incidentNibrsPrimaryInformationSourceSignal.set(null);

        //clear the tag-derived NIBRS/UCR flags so the previous incident's values can't leak into this one.
        //they are re-set as soon as getTags()/getPrimaryInformation() resolves for this incident.
        this.isNibrsIncidentSourceSignal.set(false);
        this.isUcrIncidentSourceSignal.set(false);

        //clear out the UCR data from last incident to ensure fresh reload happens
        this.incidentUcrPersonsSourceSignal.set([]);
        this.incidentUcrMainInformationSourceSignal.set(null);
        this.incidentUcrHomicideInformationSourceSignal.set(null);
        this.incidentUcrLeokaDetailModelSourceSignal.set(null);

        //reset loaded sections since they will all be unloaded
        this.incidentLoadedSectionsSourceSignal.set({});
    }

    public editIsNibrsIncident(isNibrs: boolean) {
        this.isNibrsIncidentSourceSignal.set(isNibrs);
    }

    public editShowNibrsValidation(show: boolean) {
        this.showNibrsValidationSourceSignal.set(show);
    }

    public editIsUcrIncident(isUcr: boolean) {
        this.isUcrIncidentSourceSignal.set(isUcr);
    }

    public editShowUcrIncident(show: boolean) {
        this.showUcrValidationSourceSignal.set(show);
    }

    public editIsCleryIncident(isClery: boolean) {
        this.isCleryIncidentSourceSignal.set(isClery);
    }

    public editIsTrafficStopIncident(isTrafficStop: boolean) {
        this.isTrafficStopIncidentSourceSignal.set(isTrafficStop);
    }

    public editIncidentLoadedSections(loadedSections: { [key: string]: boolean }) {
        this.incidentLoadedSectionsSourceSignal.set(loadedSections);
    }

    public editAssociationCounts(associationCounts: AssociationCountModel[]) {
        this.assocationCountsSourceSignal.set(associationCounts);
    }

    public editAgencyStateConfiguration(config: AgencyStateConfigurationModel | null) {
        this.agencyStateConfigurationSourceSignal.set(config);
    }

    public editSelectedEventRecordTypes(types: EventRecordType[]) {
        this.eventRecordTypesSourceSignal.set([...new Set(types)]);
    }

    public setEventRecordTypeSelected(type: EventRecordType, isSelected: boolean) {
        const current = this.eventRecordTypesSourceSignal();
        const next = isSelected
            ? [...new Set([...current, type])]
            : current.filter((t) => t !== type);
        this.eventRecordTypesSourceSignal.set(next);
    }

    /**
     * Reset to the hardcoded default rather than to empty.
     *
     * Called before each record's types are fetched. Clearing to `[]` blanked every section that gates on
     * "has at least one type" for the duration of that fetch — and permanently on any screen where the fetching
     * component never mounts. See {@link eventRecordTypesSourceSignal}.
     */
    public resetEventRecordTypeSelected() {
        this.eventRecordTypesSourceSignal.set([EventRecordType.Incident]);
    }

    // Toggles the refresh signal so refresh-driven sections (e.g. the common record header)
    // re-fetch their data. Used after changes that happen outside a primary-info save.
    public triggerRefresh() {
        this.refreshSourceSignal.update(v => !v);
    }

    public hasConfiguredState(states: string[]) {
        if (!this.agencyStateConfigurationComputedSignal()) return false;
        return this.agencyStateConfigurationComputedSignal()!.showAllStateFields || states.includes(this.agencyStateConfigurationComputedSignal()!.state);
    }

    // incidentId
    public createShellIncident(shellIncident: any): any {
        return this.httpClient.post(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/shell`),
            shellIncident || {}
        );
    }

    public deleteRecord(incidentId: number): any {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}`),
        )
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError("An error occurred, unable to delete record.");
                    return EMPTY;
                })
            );
    }

    public getIncidentReportById(incidentId: number) {
        return this.httpClient
            .get<IncidentPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}`
                )
            );
    }

    public setTagsForm() {
        return forkJoin([
            of(incidentTagsFields.fields),
            of(incidentTagsFields.customFields)
        ]).pipe(
            map(([tagsForm, tagsCustomFields]) => ({ tagsForm, tagsCustomFields }))
        );
    }

    public getTags(incidentId: number) {
        return forkJoin({
            tags: this.httpClient.get<number[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/tags`)),
            tagCodes: this.codeService.getCodes<CodeModel[]>(CodeType.IncidentReportTag)
        }).pipe(
            map(result => {
                this.editIsNibrsIncident(result.tagCodes.some(c => c.code === 'NIBRS' && result.tags.includes(c.id!)));
                this.editIsUcrIncident(result.tagCodes.some(c => c.code === 'UCR' && result.tags.includes(c.id!)));
                this.editIsCleryIncident(result.tagCodes.some(c => c.code === 'CLERY' && result.tags.includes(c.id!)));
                this.editIsTrafficStopIncident(result.tagCodes.some(c => c.code === 'TSTOP' && result.tags.includes(c.id!)));
                this.incidentReportTagsSourceSignal.set(result.tags);
                return result.tags;
            })
        )

    }

    public updateTags(incidentId: number, updatedTags: number[]) {
        return forkJoin({
            tags: this.httpClient.put<number[]>(this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/tags`), updatedTags),
            tagCodes: this.codeService.getCodes<CodeModel[]>(CodeType.IncidentReportTag),
            primaryInformation: this.getPrimaryInformation(incidentId),
            incidentReportIdentifierTypes: this.codeService.getCodes<CodeModel[]>(CodeType.IncidentReportIdentifierType)
        }).pipe(
            map(result => {
                this.editIsNibrsIncident(result.tagCodes.some(c => c.code === 'NIBRS' && result.tags.includes(c.id!)));
                this.editIsUcrIncident(result.tagCodes.some(c => c.code === 'UCR' && result.tags.includes(c.id!)));
                this.editIsCleryIncident(result.tagCodes.some(c => c.code === 'CLERY' && result.tags.includes(c.id!)));
                this.editIsTrafficStopIncident(result.tagCodes.some(c => c.code === 'TSTOP' && result.tags.includes(c.id!)));
                this.incidentReportTagsSourceSignal.set(result.tags);

                //synchronize updated tags reference in primary information
                result.primaryInformation.incidentReportAndIncidentReportTags = result.tags.map(t => ({
                    id: result.primaryInformation.incidentReportAndIncidentReportTags?.find(i => i.incidentReportTagId === t)?.id || 0,
                    incidentReportId: incidentId,
                    incidentReportTagId: t
                } as IncidentTagModel));

                //if a NIBRS or UCR tag is active and the incident doesn't have a NIBRS/UCR Report number, auto-add it to the incident
                const nibrsNumberType = result.incidentReportIdentifierTypes.find(t => t.code === 'NB')?.id;
                const incidentHasNibrsType = result.primaryInformation.incidentReportIdentifiers?.some(i => i.incidentReportIdentifierTypeId === nibrsNumberType);
                const ucrNumberType = result.incidentReportIdentifierTypes.find(t => t.code === 'UN')?.id;
                const incidentHasUcrType = result.primaryInformation.incidentReportIdentifiers?.some(i => i.incidentReportIdentifierTypeId === ucrNumberType);
                return {
                    ...result,
                    nibrsNumberType: nibrsNumberType,
                    addNibrsNumber: this.isNibrsIncidentComputedSignal() && !incidentHasNibrsType,
                    ucrNumberType: ucrNumberType,
                    addUcrNumber: this.isUcrIncidentComputedSignal() && !incidentHasUcrType
                }
            }),
            switchMap((result) => {
                if (result.addNibrsNumber || result.addUcrNumber) {
                    return forkJoin({
                        nibrsNumber: result.addNibrsNumber ? this.reportNumberGenerationService.generateReportNumber(ReportNumberGenerationType.NIBRSNumber)
                            : of(''),
                        ucrNumber: result.addUcrNumber ? this.reportNumberGenerationService.generateReportNumber(ReportNumberGenerationType.UCRNumber)
                            : of('')
                    })
                        .pipe(
                            switchMap((numbers) => {
                                const updatedAssignedNumbers = [...result.primaryInformation.incidentReportIdentifiers || []];
                                if (numbers.nibrsNumber)
                                    updatedAssignedNumbers.push({
                                        id: -1,
                                        identifier: numbers.nibrsNumber,
                                        incidentReportId: incidentId,
                                        incidentReportIdentifierTypeId: result.nibrsNumberType!
                                    });
                                if (numbers.ucrNumber)
                                    updatedAssignedNumbers.push({
                                        id: -1,
                                        identifier: numbers.ucrNumber,
                                        incidentReportId: incidentId,
                                        incidentReportIdentifierTypeId: result.ucrNumberType!
                                    });

                                result.primaryInformation.incidentReportIdentifiers = updatedAssignedNumbers;
                                return this.updatePrimaryInformation(incidentId, result.primaryInformation).pipe(
                                    map(() => result)
                                )
                            })
                        )
                } else
                    return of(result);
            }),
            map(result => result.tags)
        )
    }

    public setIncidentReportTypeTags(tags: CodeModel[]) {
        this.incidentReportTypeTagsSourceSignal.set(tags);
    }


    public setPrimaryInformationForm() {
        return forkJoin([
            of(incidentPrimaryInformationFields.fields),
            of(incidentPrimaryInformationFields.customFields)
        ]).pipe(
            map(([primaryInformationForm, primaryInformationCustomFields]) => ({ primaryInformationForm, primaryInformationCustomFields }))
        );
    }

    public getPrimaryInformation(incidentId: number) {
        return this.httpClient
            .get<IncidentPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/primary-info`
                )
            )
            .pipe(
                tap((primaryInfo) => {
                    this.incidentPrimaryInformationSourceSignal.set(primaryInfo);
                    if (primaryInfo.incidentReportTypes) {
                        this.incidentReportTypesSourceSignal.set(primaryInfo.incidentReportTypes);
                    }

                    if (primaryInfo.incidentReportAndIncidentReportTags) {
                        this.editIsNibrsIncident(primaryInfo.incidentReportAndIncidentReportTags.
                            some(t => t.incidentReportTag?.code === 'NIBRS'));
                        this.editIsUcrIncident(primaryInfo.incidentReportAndIncidentReportTags.
                            some(t => t.incidentReportTag?.code === 'UCR'));
                    }
                })
            );
    }

    public updatePrimaryInformation(incidentId: number, updatedPrimaryInfo: IncidentPrimaryInformationModel) {
        return this.httpClient
            .put<IncidentPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/primary-info`
                ),
                updatedPrimaryInfo
            ).pipe(
                tap((primaryInfo) => {
                    this.incidentPrimaryInformationSourceSignal.update(() => primaryInfo);
                    if (primaryInfo.incidentReportTypes) {
                        this.incidentReportTypesSourceSignal.set(primaryInfo.incidentReportTypes);
                    }
                    this.refreshSourceSignal.update(v => !v);
                })
            );
    }

    public getAgencyOfficersFromIncidentId(incidentId: number) {
        return this.httpClient
            .get<IncidentPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/primary-info`
                )
            )
            .pipe(
                switchMap((data) =>
                    this.agencyService.getAgencyOfficers(data.primaryAgency?.organizationId ?? 0).pipe(
                        catchError(() => {
                            return EMPTY;
                        })
                    )
                ),

                shareReplay({ bufferSize: 1, refCount: true })
            )
    }

    //#region assignments
    public setAssignmentsForm() {
        return forkJoin([
            of(incidentAssignmentsFields.fields),
            of(incidentAssignmentsFields.customFields)
        ]).pipe(
            map(([form, assignmentsCustomFields]) => ({ form, assignmentsCustomFields }))
        );
    }


    public getAssignments(incidentId: number) {
        return this.httpClient.get<IncidentAssignmentModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/assignments`));
    }

    public getDivisionForOfficerId(officerId: number) {
        return this.httpClient
            .get<number | null>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/assignments/${officerId}/division`
                )
            );
    }

    public deleteAssignments(assignmentId: number) {
        return this.httpClient
            .delete<IncidentAssignmentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/assignments/${assignmentId}`
                )
            );
    }

    public updateAssignment(updatedNrfAssignment: IncidentAssignmentModel, assignmentId: number) {
        return this.httpClient
            .put<IncidentAssignmentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/assignments/${assignmentId}`
                ),
                updatedNrfAssignment
            );
    }

    public addAssignment(newNrfAssignment: IncidentAssignmentModel) {
        return this.httpClient
            .post<IncidentAssignmentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/assignments`
                ),
                newNrfAssignment
            );
    }

    public getIncidentAssignmentsByEmployeeId(employeeId: number) {
        return this.httpClient.get<IncidentAssignmentModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/assignments/employee/${employeeId}`)
        ).pipe(
            tap((incidentReportAssignments) => {
                this.incidentReportAssignmentsSourceSignal.set(incidentReportAssignments)
            })
        );
    }

    public getFilteredAssignments(filter: IncidentAssignmentsFilterModel) {
        return this.httpClient.post<IncidentAssignmentModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/assignments/filter`), filter
        ).pipe(
            tap((assignments) => {
                this.filteredAssignmentsSourceSignal.set(assignments)
            })
        );
    }

    public setAssignmentsLoading(value: boolean) {
        this.assignmentsLoadingSignal.set(value);
    }

    public setScrollToAssignment(value: string) {
        this.scrollToAssignmentSourceSignal.set(value);
    }
    //#endregion

    public getLawEnforcementAccreditations(incidentId: number) {
        return this.httpClient.get<IncidentReportLawEnforcementAccreditationDto[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/law-enforcement-accreditations`));
    }

    public addLawEnforcementAccreditations(incidentId: number, incidentReportLawEnforcementAgencyDto: IncidentReportLawEnforcementAccreditationDto[]) {
        return this.httpClient
            .post<IncidentReportLawEnforcementAccreditationDto[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/law-enforcement-accreditations`
                ),
                incidentReportLawEnforcementAgencyDto
            );
    }

    public getCommissionLawEnforcementAccreditations(incidentId: number) {
        return this.httpClient.get<IncidentReportCommissionAccreditationLawEnforcementAgencyDto[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/commission-law-enforcement-accreditations`));
    }

    public addCommissionLawEnforcementAccreditations(incidentId: number, incidentReportLawEnforcementAgencyDto: IncidentReportCommissionAccreditationLawEnforcementAgencyDto[]) {
        return this.httpClient
            .post<IncidentReportCommissionAccreditationLawEnforcementAgencyDto[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/commission-law-enforcement-accreditations`
                ),
                incidentReportLawEnforcementAgencyDto
            );
    }

    public setProfessionalStandardsForm() {
        return forkJoin([
            of(incidentProfessionalStandards.fields),
            of(incidentProfessionalStandards.customFields)
        ]).pipe(
            map(([professionalStandardsForm, professionalStandardsCustomFields]) => ({ professionalStandardsForm, professionalStandardsCustomFields }))
        );
    }

    public setDatesForm() {
        return forkJoin([
            of(incidentDatesFields.fields),
            of(incidentDatesFields.customFields)
        ]).pipe(
            map(([datesForm, datesCustomFields]) => ({ datesForm, datesCustomFields }))
        );
    }

    public getIncidentAndLocationById(incidentAndLocationId: number): Observable<IncidentLocationModel> {
        return this.httpClient.get<IncidentLocationModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/address/${incidentAndLocationId}`
        )
    }


    public getIncidentLocations(incidentReportId: number) {
        return this.httpClient
            .get<IncidentLocationModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentReportId}/addresses`
                )
            ).pipe(
                tap(addresses => {
                    this.incidentLocationsSourceSignal.set(addresses)
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.IncidentReportAndAddressType ? { ...c, count: addresses.length } : c
                        ));
                }));
    }

    public addIncidentAndLocation(nrfId: number, add: IncidentLocationModel) {
        return this.httpClient
            .post<IncidentLocationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${nrfId}/location`
                ),
                add
            ).pipe(
                tap(result => {
                    this.incidentLocationsSourceSignal.update(nrfAddress => [...nrfAddress, result]);
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.IncidentReportAndAddressType ? { ...c, count: c.count + 1 } : c
                        ));
                })
            );
    }

    public updateIncidentAndLocation(location: IncidentLocationModel) {
        return this.httpClient.put<IncidentLocationModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/address/${location.id}`, location
        ).pipe(
            tap(updatedLocation => {
                this.incidentLocationsSourceSignal.update(items =>
                    items.map(item => {
                        if (item.id === updatedLocation.id) {
                            return updatedLocation;
                        }

                        if (updatedLocation.isPrimary) {
                            return { ...item, isPrimary: false };
                        }

                        return item;
                    })
                );
            })
        );
    }

    public deleteIncidentAndLocation(IncidentLocationModelId: number) {
        return this.httpClient
            .delete(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/addresses/${IncidentLocationModelId}`
                ))
            .pipe(
                tap(() => {
                    this.incidentLocationsSourceSignal.update(nrfAddress => nrfAddress.filter(address => address.id !== IncidentLocationModelId));
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.IncidentReportAndAddressType ? { ...c, count: c.count - 1 } : c
                        ));
                })
            );
    }

    public getDates(incidentId: number) {
        return this.httpClient.get<IncidentDatesModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/dates`))
            .pipe(
                map(result => {
                    const datesInfo: IncidentDatesModel = {
                        id: result.id,
                        createdOn: result.createdOn ? new Date(result.createdOn) : undefined,
                        receivedOn: result.receivedOn ? new Date(result.receivedOn) : undefined,
                        occurredFrom: result.occurredFrom ? new Date(result.occurredFrom) : undefined,
                        occurredTo: result.occurredTo ? new Date(result.occurredTo) : undefined,
                        dispatchedOn: result.dispatchedOn ? new Date(result.dispatchedOn) : undefined,
                        arrivedOn: result.arrivedOn ? new Date(result.arrivedOn) : undefined,
                        clearedOn: result.clearedOn ? new Date(result.clearedOn) : undefined
                    };
                    return datesInfo;
                })
            );
    }

    public updateDates(updatedDates: IncidentDatesModel, incidentId: number) {
        return this.httpClient
            .put<IncidentDatesModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${incidentId}/dates`
                ),
                updatedDates
            )
            .pipe(
                //map((result) => new NrfDates.Edit(result)),
                map(result => {
                    const datesInfo: IncidentDatesModel = {
                        id: result.id,
                        createdOn: result.createdOn ? new Date(result.createdOn) : undefined,
                        receivedOn: result.receivedOn ? new Date(result.receivedOn) : undefined,
                        occurredFrom: result.occurredFrom ? new Date(result.occurredFrom) : undefined,
                        occurredTo: result.occurredTo ? new Date(result.occurredTo) : undefined,
                        dispatchedOn: result.dispatchedOn ? new Date(result.dispatchedOn) : undefined,
                        arrivedOn: result.arrivedOn ? new Date(result.arrivedOn) : undefined,
                        clearedOn: result.clearedOn ? new Date(result.clearedOn) : undefined
                    };
                    return datesInfo;
                }),
            );
    }

    public setModusOperandisForm() {
        return forkJoin([
            of(incidentModusOperandiFields.fields),
            of(incidentModusOperandiFields.customFields)
        ]).pipe(
            map(([modusOperandisForm, modusOperandisCustomFields]) => ({ modusOperandisForm, modusOperandisCustomFields }))

        );
    }

    public getModusOperandis(incidentId: number) {
        return this.httpClient
            .get<IncidentModusOperandiModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/modus-operandis`)
            ).pipe(
                tap(result => {
                    const uniqueCollectionSequenceCount = new Set(result.map(mo => mo.collectionSequence)).size;
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c => c.associationCountType === CountType.ModusOperandiAndIncidentReportType ? { ...c, count: uniqueCollectionSequenceCount } : c
                        ));
                })
            );
    }

    public updateModusOperandis(updatedModusOperandis: IncidentModusOperandiModel[], incidentId: number) {
        return this.httpClient
            .put<IncidentModusOperandiModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/modus-operandis`),
                updatedModusOperandis
            ).pipe(
                tap(result => {
                    const uniqueCollectionSequenceCount = new Set(result.map(mo => mo.collectionSequence)).size;
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c => c.associationCountType === CountType.ModusOperandiAndIncidentReportType ? { ...c, count: uniqueCollectionSequenceCount } : c
                        ));
                })
            );
    }


    public savePerson(p: PersonIncidentModel) {
        return this.httpClient
            .post<PersonIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/incident-report-person`), p
            )
            .pipe(
                tap((response: PersonIncidentModel) => {
                    this.incidentPersonsSourceSignal.update(persons => [...persons, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
            );
    }

    public updatePerson(p: PersonIncidentModel) {
        return this.httpClient
            .put<PersonIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-report-person?personAndIncidentReportId=${p.id}`
                ),
                p
            )
            .pipe(
                tap((response: PersonIncidentModel) => {
                    this.incidentPersonsSourceSignal.update(persons => persons.map(person => person.id === response.id ? response : person));
                }),
            );
    }

    public setIncidentPersonPopoutForm() {
        return forkJoin([
            of(incidentPersonFields.fields),
            of(incidentPersonFields.customFields)
        ]).pipe(
            map(([incidentPersonFields, incidentPersonCustomFields]) => ({ incidentPersonFields, incidentPersonCustomFields }))
        );
    }
    
    public getPersons(incidentId: number): Observable<PersonIncidentModel[]> {
        // will update model once its solidified
        return this.httpClient
            .get<PersonIncidentModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-report-person/incident-report/${incidentId}`
                )
            )
            .pipe(
                tap((persons) => {
                    // Populate the signal with the fetched data
                    this.incidentPersonsSourceSignal.set(persons);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.incidentPersonsSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return of([]);
                })
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deletePersonAssociation(personAndIncidentReportId: number): Observable<void> {
        return this.httpClient.delete<void>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/association/incident-report-person?personAndIncidentReportId=${personAndIncidentReportId}`
            )
        )
            .pipe(
                tap(() => {
                    this.incidentPersonsSourceSignal.update(persons => persons.filter(person => person.id !== personAndIncidentReportId));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPersonAndIncidentReportType ? { ...c, count: c.count - 1 } : c
                    ));
                }),
            );
    }

    public saveIncidentAssociation(assoc: IncidentIncidentModel) {
        const model = {
            ...assoc,
            incidentReport1Id: assoc.incidentReport1Id,
            incidentReport2Id: assoc.incidentReport2Id
        };
        return this.httpClient
            .post<IncidentIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/incident-incident`), model
            )
            .pipe(
                tap((response) => {
                    const newAssociation = {
                        ...assoc,
                        id: response.id
                    };
                    this.incidentIncidentsSourceSignal.update(nrfIncidents => [...nrfIncidents, newAssociation]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentAssociation(assoc: IncidentIncidentModel) {
        return this.httpClient
            .put<IncidentIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-incident?incidentReportAndIncidentReportId=${assoc.id}`
                ),
                assoc
            )
            .pipe(
                tap((response) => {
                    const updatedAssociation = {
                        ...assoc,
                        id: response.id
                    };
                    this.incidentIncidentsSourceSignal.update(personIncidents => personIncidents.map(personIncident => personIncident.id === response.id ? updatedAssociation : personIncident));
                })
            );
    }

    public getIncidentAssociations(incidentId: number) {
        // will update model once its solidified
        return this.httpClient
            .get<IncidentIncidentModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-incident?incidentReport1Id=${incidentId}`
                )
            )
            .pipe(
                tap((associations) => {
                    // Transform the fetched associations before updating the BehaviorSubject if needed
                    this.incidentIncidentsSourceSignal.set(associations.map(assoc => ({
                        id: assoc.id,
                        associatedOn: assoc.associatedOn,
                        comment: assoc.comment,
                        incidentReport1: assoc.incidentReport1,
                        incidentReport1Id: assoc.incidentReport1Id,
                        incidentReport2: assoc.incidentReport2,
                        incidentReport2Id: assoc.incidentReport2Id,
                        isIncidentReport1Master: incidentId === assoc.incidentReport1Id,
                        incidentReportAndIncidentReportAssociationTypes: assoc.incidentReportAndIncidentReportAssociationTypes
                    } as IncidentIncidentModel)));

                })
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deleteIncidentAssociation(incidentReportAndIncidentReportId: number): any {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/association/incident-incident?incidentReportAndIncidentReportId=${incidentReportAndIncidentReportId}`
            )
        )
            .pipe(
                tap(() => {
                    this.incidentIncidentsSourceSignal.update(nrfIncidents => nrfIncidents.filter(nrfIncident => nrfIncident.id !== incidentReportAndIncidentReportId));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndIncidentReportType ? { ...c, count: c.count - 1 } : c
                    ));
                }));
    }

    //incident-vehicle associations
    public saveIncidentVehicle(v: VehicleIncidentModel) {
        return this.httpClient
            .post<VehicleIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/vehicle-incident-report`), v
            )
            .pipe(
                tap((response: VehicleIncidentModel) => {
                    this.incidentVehiclesSourceSignal.update(vehicles => [...vehicles, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
            );
    }

    public updateIncidentVehicle(v: VehicleIncidentModel) {
        return this.httpClient
            .put<VehicleIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/vehicle-incident-report/${v.id}`
                ),
                v
            )
            .pipe(
                tap((response: VehicleIncidentModel) => {
                    this.incidentVehiclesSourceSignal.update(vehicles => vehicles.map(vehicle => vehicle.id === response.id ? response : vehicle));
                }),
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deleteIncidentVehicle(id: number) {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/association/vehicle-incident-report/${id}`
            )
        )
            .pipe(
                tap(() => {
                    this.incidentVehiclesSourceSignal.update(vehicles => vehicles.filter(vehicle => vehicle.id !== id));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndIncidentReportType ? { ...c, count: c.count - 1 } : c
                    ));
                }),
            );
    }

    public getIncidentVehicle(id: number) {
        return this.httpClient
            .get<VehicleIncidentModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/vehicle-incident-report?incidentReportId=${id}`
                )
            )
            .pipe(
                tap((persons) => {
                    this.incidentVehiclesSourceSignal.set(persons);
                }),
                catchError(() => {
                    this.incidentVehiclesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public getVehiclePersonAssociation(incidentId?: number, itemId?: number, personId?: number) {
        return this.httpClient
            .get<VehiclePersonModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/vehicle-person`,
                    this.getVehiclesPersonsQueryString(incidentId, itemId, personId)
                )
            )
            .pipe(
                tap((persons) => {
                    this.incidentVehiclesPersonsSourceSignal.set(persons);
                }),
                catchError(() => {
                    this.incidentVehiclesPersonsSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public saveVehiclePersonAssociation(pv: VehiclePersonModel) {
        return this.httpClient
            .post<VehiclePersonModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/vehicle-person`), pv
            )
            .pipe(
                tap((response: VehiclePersonModel) => {
                    this.incidentVehiclesPersonsSourceSignal.update(vehiclesPersons => [...vehiclesPersons, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndPersonType ? { ...c, count: c.count + 1 } : c
                    ));

                    if(pv.incidentReportId){
                        this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                            c.associationCountType === CountType.AssociationVehicleAssociationIncidentReport ? { ...c, count: c.count + 1 } : c
                    ));
                    }
                }),
            )
    }

    public updateVehiclePersonAssociation(pv: VehiclePersonModel) {
        return this.httpClient
            .put<VehiclePersonModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/vehicle-person?personAndVehicleId=${pv.id}`
                ),
                pv
            )
            .pipe(
                tap((response: VehiclePersonModel) => {
                    this.incidentVehiclesPersonsSourceSignal.update(vehiclesPersons => vehiclesPersons.map(vehiclePerson => vehiclePerson.id === response.id ? response : vehiclePerson));
                }),
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deleteVehiclePersonAssociation(personAndVehicleId: number) {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/association/vehicle-person?personAndVehicleId=${personAndVehicleId}`
            )
        )
            .pipe(
                tap(() => {
                    this.incidentVehiclesPersonsSourceSignal.update(vehiclesPersons => vehiclesPersons.filter(vehiclePerson => vehiclePerson.id !== personAndVehicleId));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAndPersonType ? { ...c, count: c.count - 1 } : c
                    ));

                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationVehicleAssociationIncidentReport ? { ...c, count: c.count - 1 } : c
                    ));
                }),
            );
    }

    public updateassocationCountsSourceSignal(countType: CountType, count: number): void {
        this.assocationCountsSourceSignal.update(counts => counts.map(c =>
            c.associationCountType === countType ? { ...c, count: c.count + count } : c
        ));
    }

    // Private functions
    private getVehiclesPersonsQueryString(incidentId?: number, itemId?: number, personId?: number) {
        const queryStringParts: string[] = [];

        if (incidentId)
            queryStringParts.push(`incidentReportId=${incidentId}`)
        if (itemId)
            queryStringParts.push(`vehicleDetailId=${itemId}`)
        if (personId)
            queryStringParts.push(`personId=${personId}`)

        return queryStringParts ? '?' + queryStringParts.join('&') : '';
    }

    public setIncidentReportActiveNarrative(narrative: NarrativeModel | null): void {
        this.incidentReportActiveNarrativeSignal.set(narrative);
    }

    public updateNarrativeReviewSignal(updatedReview: IncidentReviewModel) {
        // Update array of narrative reviews
        if (this.incidentReportNarrativeReviewsSourceSignal().length > 0) {
            this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                return currentReviews.map(currentReview =>
                    currentReview.id === updatedReview.id ? updatedReview : currentReview
                );
            });
        }

        this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
            if (currentNarrative?.review) {
                return {
                    ...currentNarrative,
                    review: updatedReview
                };
            }
            return currentNarrative;
        });
    }

    public getIncidentReportNarrativeById(incidentReportId: number, narrativeId: number) {
        return this.httpClient
            .get<IncidentNarrativeModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/narratives/${incidentReportId}/${narrativeId}`
                )
            )
    }

    public getIncidentReportNarratives(incidentId: number): Observable<NarrativeModel[]> {
        return this.httpClient.get<NarrativeModel[]>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives/incidents/${incidentId}`)
        ).pipe(
            switchMap((narratives: NarrativeModel[]) => {
                if (!narratives || !narratives.length) return of([]);

                const allUserIds = [
                    ...narratives.map(n => n.createdByUserId),
                    ...narratives.map(n => n.updatedByUserId),
                    ...narratives.map(n => n.textUpdatedById),
                    ...narratives.map(n => n.approvedById) 
                ].filter(id => !!id); 

                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return forkJoin({
                    userNames: this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                        catchError(() => {
                            return of({} as Record<number, string>); 
                        })
                    ),
                    employees: this.personService.getEmployeesByUserIds(uniqueUserIds).pipe(
                        catchError(() => {
                            return of([] as EmployeeModel[]); 
                        })
                    )
                }).pipe(
                    map(({ userNames, employees }) => {

                    const employeeDict = Array.isArray(employees) 
                        ? employees.reduce((acc, emp) => {
                            if (emp.userId !== undefined) {
                                acc[emp.userId] = emp;
                            }
                            return acc;
                        }, {} as Record<number, EmployeeModel>)
                        : employees;

                        return narratives.map((narrative: NarrativeModel) => {
                            return {
                                ...narrative,
                                createdByUserName: userNames[narrative.createdByUserId!],
                                createdByRank: employeeDict[narrative.createdByUserId!]?.officerRank?.code,
                                createdByBadge: employeeDict[narrative.createdByUserId!]?.badgeNumber,
                                updatedByUserName: userNames[narrative.updatedByUserId!],
                                updatedByRank: employeeDict[narrative.updatedByUserId!]?.officerRank?.code,
                                updatedByBadge: employeeDict[narrative.updatedByUserId!]?.badgeNumber,
                                textUpdatedByUserName: userNames[narrative.textUpdatedById!],
                                textUpdatedByRank: employeeDict[narrative.textUpdatedById!]?.officerRank?.code,
                                textUpdatedByBadge: employeeDict[narrative.textUpdatedById!]?.badgeNumber,
                                approvedByUserName: userNames[narrative.approvedById!],
                                isApproved: !!narrative.approvedById && narrative.approvedOn !== undefined
                            };
                        });
                    })
                );
            }),
            tap((narratives: NarrativeModel[]) => {
                for (const narrative of narratives) {
                    narrative.createdOn = new Date(narrative.createdOn!);
                    narrative.updatedOn = new Date(narrative.updatedOn!);
                    narrative.approvedOn = narrative.approvedOn ? new Date(narrative.approvedOn) : undefined;
                    narrative.textUpdatedOn = narrative.textUpdatedOn ? new Date(narrative.textUpdatedOn!) : undefined;
                }
                this.incidentReportNarrativesSourceSignal.set(narratives);
            })
        );
    }

    public getIncidentReportNarrativeContent(narrativeId: number): Observable<Blob> {
        return this.httpClient.get<Blob>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives/${narrativeId}/content`),
            {
                responseType: 'blob' as 'json'
            }
        );
    }

    public filterNarrativeContentByIds(filter: NarrativeContentFilterModel): Observable<number[]> {
        return this.httpClient.post<number[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/narratives/search`), filter
        );
    }

    public createIncidentReportNarrative(narrativeToBeCreated: NarrativeModel): Observable<NarrativeModel> {
        return this.httpClient.post<NarrativeModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives`),
            narrativeToBeCreated
        ).pipe(
            switchMap((createdNarrative: NarrativeModel) => {
                if (!createdNarrative) return of({} as NarrativeModel);

                const allUserIds = [
                    createdNarrative.createdByUserId,
                    createdNarrative.updatedByUserId,
                    createdNarrative.textUpdatedById,
                    createdNarrative.approvedById
                ].filter(id => !!id); 

                const uniqueUserIds = [...new Set(allUserIds)] as number[];

                return forkJoin({
                    userNames: this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                        catchError(() => {
                            return of({} as Record<number, string>); 
                        })
                    ),
                    employees: this.personService.getEmployeesByUserIds(uniqueUserIds).pipe(
                        catchError(() => {
                            return of([] as EmployeeModel[]); 
                        })
                    )
                }).pipe(
                    map(({ userNames, employees }) => {
                        const employeeDict = Array.isArray(employees) 
                            ? employees.reduce((acc, emp) => {
                                if (emp.userId !== undefined) {
                                    acc[emp.userId] = emp;
                                }
                                return acc;
                            }, {} as Record<number, EmployeeModel>)
                            : employees;

                        return {
                            ...createdNarrative,
                            createdByUserName: userNames[createdNarrative.createdByUserId!],
                            createdByRank: employeeDict[createdNarrative.createdByUserId!]?.officerRank?.code,
                            createdByBadge: employeeDict[createdNarrative.createdByUserId!]?.badgeNumber,
                            updatedByUserName: userNames[createdNarrative.updatedByUserId!],
                            updatedByRank: employeeDict[createdNarrative.updatedByUserId!]?.officerRank?.code,
                            updatedByBadge: employeeDict[createdNarrative.updatedByUserId!]?.badgeNumber,
                            textUpdatedByUserName: userNames[createdNarrative.textUpdatedById!],
                            textUpdatedByRank: employeeDict[createdNarrative.textUpdatedById!]?.officerRank?.code,
                            textUpdatedByBadgee: employeeDict[createdNarrative.textUpdatedById!]?.badgeNumber,
                            approvedByUserName: userNames[createdNarrative.approvedById!],
                            isApproved: !!createdNarrative.approvedById && createdNarrative.approvedOn !== undefined
                        };
                    })
                );
            }),
            tap((createdNarrative: NarrativeModel) => {
                createdNarrative.createdOn = new Date(createdNarrative.createdOn!);
                createdNarrative.updatedOn = new Date(createdNarrative.updatedOn!);
                createdNarrative.approvedOn = createdNarrative.approvedOn ? new Date(createdNarrative.approvedOn) : undefined;
                createdNarrative.textUpdatedOn = createdNarrative.textUpdatedOn ? new Date(createdNarrative.textUpdatedOn!) : undefined;

                this.incidentReportNarrativesSourceSignal.update(narratives => [...narratives, createdNarrative]);

                this.incidentReportActiveNarrativeSignal.set(createdNarrative);

                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNarrative ? { ...c, count: c.count + 1 } : c
                ));
            })
        );
    }

    public updateIncidentReportNarrative(narrativeToBeUpdated: NarrativeModel): Observable<NarrativeModel> {
        return this.httpClient.put<NarrativeModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives/${narrativeToBeUpdated.id}`),
            narrativeToBeUpdated
        )
            .pipe(
                switchMap((updatedNarrative: NarrativeModel) => {
                    if (!updatedNarrative) return of({} as NarrativeModel);

                    const allUserIds = [
                        updatedNarrative.createdByUserId,
                        updatedNarrative.updatedByUserId,
                        updatedNarrative.textUpdatedById
                    ];
                    const uniqueUserIds = [...new Set(allUserIds)] as number[];

                    return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                        map((userNames: Record<number, string>) => {
                            return {
                                ...updatedNarrative,
                                createdByUserName: userNames[updatedNarrative.createdByUserId!],
                                updatedByUserName: userNames[updatedNarrative.updatedByUserId!],
                                textUpdatedByUserName: userNames[updatedNarrative.textUpdatedById!],
                                approvedByUserName: userNames[updatedNarrative.approvedById!],
                                isApproved: !!updatedNarrative.approvedById && updatedNarrative.approvedOn !== undefined
                            }
                        }))
                }),
                tap((updatedNarrative: NarrativeModel) => {
                    updatedNarrative.createdOn = new Date(updatedNarrative.createdOn!);
                    updatedNarrative.updatedOn = new Date(updatedNarrative.updatedOn!);
                    updatedNarrative.approvedOn = updatedNarrative.approvedOn ? new Date(updatedNarrative.approvedOn) : undefined;
                    updatedNarrative.textUpdatedOn = new Date(updatedNarrative.textUpdatedOn!);

                    this.incidentReportNarrativesSourceSignal.update(narratives => narratives.map(narrative => narrative.id === updatedNarrative.id ? updatedNarrative : narrative));
                })
            );

    }

    public deleteIncidentReportNarrative(narrativeId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives/${narrativeId}`),
        ).pipe(
            tap(() => {
                this.incidentReportNarrativesSourceSignal.update(narratives => narratives.filter(narrative => narrative.id !== narrativeId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNarrative ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public updateNarrativeSequences(narratives: IncidentNarrativeModel[]): Observable<any> {
        return this.httpClient.put(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/narratives/sequences`),
            narratives
        )
    }

    public getDraftNarratives(filter: IncidentNarrativeDraftFilterModel) {
        const url = this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/narratives/drafts`);

        return this.httpClient.post<IncidentNarrativeModel[]>(url, filter).pipe(
            tap((narratives) => {
                this.narrativeDraftsSourceSignal.set(narratives);
            })
        );
    }

    public getUnsubmittedNarratives(filter: UnsubmittedFilterModel): Observable<IncidentNarrativeModel[]> {
        const url = this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/narratives/unsubmitted`);

        return this.httpClient.post<IncidentNarrativeModel[]>(url, filter).pipe(
            switchMap((narratives: IncidentNarrativeModel[]) => {
                if (!narratives || !narratives.length) return of([]);

                const createdByUserIds = narratives.map(n => n.narrative?.createdByUserId);
                const updatedByUserIds = narratives.map(n => n.narrative?.updatedByUserId);
                const textUpdatedByUserIds = narratives.map(n => n.narrative?.textUpdatedById);

                const allUserIds = [...createdByUserIds, ...updatedByUserIds, ...textUpdatedByUserIds];
                
                const uniqueUserIds = [...new Set(allUserIds)].filter(id => !!id) as number[];

                return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                    map((userNames: Record<number, string>) => {
                        
                        const getInitials = (name: string | undefined): string => {
                            if (!name) return '';
                            const parts = name.trim().split(' ');
                            if (parts.length === 0) return '';
                            if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
                            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
                        };

                        return narratives.map((narrative) => {
                            const cName = userNames[narrative.narrative?.createdByUserId ?? 0] || '';
                            const uName = userNames[narrative.narrative?.updatedByUserId ?? 0] || '';
                            const tName = userNames[narrative.narrative?.textUpdatedById ?? 0] || '';

                            return {
                                ...narrative,
                                createdByUserName: cName,
                                updatedByUserName: uName,
                                textUpdatedByUserName: tName,

                                createdByInitials: getInitials(cName),
                                updatedByInitials: getInitials(uName),
                                textUpdatedByInitials: getInitials(tName)
                            };
                        });
                    })
                );
            }),
            tap((narratives: IncidentNarrativeModel[]) => {
                for (const narrative of narratives) {
                    if (narrative.narrative?.createdOn) narrative.narrative.createdOn = new Date(narrative.narrative.createdOn);
                    if (narrative.narrative?.updatedOn) narrative.narrative.updatedOn = new Date(narrative.narrative.updatedOn);
                    if (narrative.narrative?.textUpdatedOn) narrative.narrative.textUpdatedOn = new Date(narrative.narrative.textUpdatedOn);
                }
                
                this.unsubmittedNarrativesSourceSignal.set(narratives);
            })
        );
    }


    public setSupervisorForm() {
        return forkJoin([
            of(supervisorFields.fields),
            of(supervisorFields.customFields)
        ]).pipe(
            map(([supervisorForm, supervisorCustomFields]) => ({ supervisorForm, supervisorCustomFields }))
        );
    }

    public setNrfUnitOfficersForm() {
        return forkJoin([
            of(incidentUnitOfficerFields.fields),
            of(incidentUnitOfficerFields.customFields)
        ]).pipe(
            map(([fields, customFields]) => ({ fields, customFields })))
    }

    public getIncidentUnits(id: number) {
        return this.httpClient
            .get<UnitOfficerSummaryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/${id}/unit-officers-summary`
                )
            )
            .pipe(
                tap((incidents) => {
                    this.incidentUnitsAndOfficersSourceSignal.set(incidents);
                    this.assocationCountsSourceSignal.update(counts =>
                        counts.map(c =>
                            c.associationCountType === CountType.UnitAndIncidentReportType
                                ? { ...c, count: incidents.length }
                                : c
                        )
                    );
                }),
                catchError(() => {
                    this.incidentUnitsAndOfficersSourceSignal.set([]);
                    return of([]);
                })
            );
    }



    public getUnitsOfficers(id: number) {
        return this.httpClient
            .get<UnitIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/unit-officers?id=${id}`
                )
            )
            .pipe(
                map(result => {
                    const officersInfo: UnitIncidentModel = {
                        id: result.id,
                        organizationId: result.organizationId,
                        fleetVehicleId: result.fleetVehicleId,
                        incidentReportId: result.incidentReportId,
                        sequence: result.sequence,
                        unitId: result.unitId
                    };
                    const unitOfficers: UnitOfficerModel[] = [];
                    result.unitOfficers?.forEach((element) => {
                        const unitOfficer: UnitOfficerModel = {
                            id: element.id,
                            unitIncidentReportId: element.unitIncidentReportId,
                            agencyDivisionId: element.agencyDivisionId,
                            assignment: element.assignment,
                            employeeId: element.employeeId,
                            isPrimary: element.isPrimary,
                            isSupervisor: element.isSupervisor
                        };
                        unitOfficer.dispatched = element.dispatched == null ? null : new Date(element.dispatched);
                        unitOfficer.cleared = element.cleared == null ? null : new Date(element.cleared);
                        unitOfficer.arrived = element.arrived == null ? null : new Date(element.arrived);
                        unitOfficers.push(unitOfficer);
                    });
                    officersInfo.unitOfficers = unitOfficers;
                    return officersInfo;
                }),
            );
    }

    // this was coded in a way that re fetches the data on update
    public updateUnitsOfficers(unit: UnitIncidentModel) {
        return this.httpClient
            .put<UnitIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/unit-officers?id=${unit.id}`
                ),
                unit
            );
    }

    // this was coded in a way that re fetches the data on save
    public saveUnitsOfficers(unit: UnitIncidentModel) {
        return this.httpClient
            .post<UnitIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/unit-officers`
                ),
                unit
            );
    }

    public deleteUnits(units: number[]) {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: units
        };


        return this.httpClient
            .delete<UnitIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/incidents/unit-officers`), httpOptions);
    }

    public updateIncidentReportSupervisorById(incidentReportId: number, supervisorId: number) {
        return this.httpClient
            .put<IncidentPrimaryInformationModel>(
                `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/${incidentReportId}/supervisor`,
                supervisorId
            )
            .pipe(
                tap((incidentReport) => {
                    this.incidentPrimaryInformationSourceSignal.update(currentIncidentReport =>
                        currentIncidentReport
                            ? { ...currentIncidentReport, supervisorId: incidentReport.supervisorId, supervisor: incidentReport.supervisor }
                            : currentIncidentReport
                    );
                })
            )

    }

    public setIncidentReviewForm() {
        return forkJoin([
            of(incidentReviewFields.fields),
            of(incidentReviewFields.customFields)
        ]).pipe(
            map(([incidentReviewForm, incidentReviewCustomFields]) => ({ incidentReviewForm, incidentReviewCustomFields }))
        );
    }

    public setIncidentReviewersForm() {
        return forkJoin([
            of(incidentReviewersFields.fields),
            of(incidentReviewersFields.customFields)
        ]).pipe(
            map(([incidentReviewersForm, incidentReviewersCustomFields]) => ({ incidentReviewersForm, incidentReviewersCustomFields }))
        );
    }

    //#region reviews
    public getIncidentReviewByIncidentId(incidentId: number) {
        return this.httpClient.get<IncidentReviewModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/review`)
        ).pipe(
            tap((incidentReportReview) => {
                if (incidentReportReview) {
                    this.incidentReportReviewSourceSignal.set(incidentReportReview)
                } else {
                    const emptyIncidentReportReview: IncidentReviewModel = {
                        id: 0,
                        incidentReportId: incidentId,
                        reviewEmployeeId: 0,
                        approvers: [],
                        approverGroups: [],
                        incidentReportReviewComments: []
                    };
                    this.incidentReportReviewSourceSignal.set(emptyIncidentReportReview)
                }
            })
        );
    }

    public getIncidentReviewByReviewId(reviewId: number) {
        return this.httpClient.get<IncidentReviewModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/review/${reviewId}`)
        ).pipe(
            tap((incidentReportReview) => {
                if (incidentReportReview) {
                    this.incidentReportReviewSourceSignal.set(incidentReportReview)
                } else {
                    const emptyIncidentReportReview: IncidentReviewModel = {
                        id: 0,
                        incidentReportId: 0,
                        reviewEmployeeId: 0,
                        approvers: [],
                        approverGroups: [],
                        incidentReportReviewComments: []
                    };
                    this.incidentReportReviewSourceSignal.set(emptyIncidentReportReview)
                }
            })
        );
    }

    public getIncidentNarrativeReviewsByIncidentReportId(incidentReportId: number) {
        return this.httpClient.get<IncidentReviewModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportId}/narrative-reviews`)
        ).pipe(
            tap((incidentReportReview) => {
                this.incidentReportNarrativeReviewsSourceSignal.set(incidentReportReview)
            })
        );
    }

    public setReviewLoading(value: boolean) {
        this.incidentReportReviewsLoadingSignal.set(value);
    }

    public getIncidentReviewsByEmployeeId(employeeId: number) {
        return this.httpClient.get<IncidentReviewModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/review/employee/${employeeId}`)
        ).pipe(
            tap((incidentReportReview) => {
                this.incidentReportReviewsSourceSignal.set(incidentReportReview)
            })
        );
    }
    public getUnsubmittedIncident(filter: UnsubmittedFilterModel) {
        const url = this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/review/unsubmitted`);

        return this.httpClient.post<IncidentModel[]>(url, filter).pipe(
            tap((incidentReports) => {
                this.unsubmittedIncidentReportsSourceSignal.set(incidentReports);
            })
        );
    }

    public getFilteredIncidentReports(filter: IncidentReportFilterModel) {
        return this.httpClient.post<IncidentModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/filtered`), filter
        ).pipe(
            tap((incidentReports) => {
                this.filteredIncidentReportSourceSignal.set(incidentReports)
            })
        );
    }


    public setIncidentReportReviewsFilteredCount(value: number) {
        this.incidentReportReviewsFilteredCountSourceSignal.set(value);
    }


    public getFilteredIncidentReviews(filter: IncidentReviewFilterModel) {
        return this.httpClient.post<IncidentReviewModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/review/filter`), filter
        ).pipe(
            tap((incidentReportReview) => {
                this.incidentReportReviewsSourceSignal.set(incidentReportReview)
            })
        );
    }

    public updateIncidentReviewApproversByReviewId(reviewId: number, review: IncidentReviewModel) {
        return this.httpClient.put<IncidentReviewModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/${reviewId}/approvers`, review
        ).pipe(
            tap((updatedReview: IncidentReviewModel) => {

                this.incidentReportReviewSourceSignal.update(currentReview => {
                    if (currentReview) {
                        return { ...currentReview, approvers: updatedReview.approvers, approverGroups: updatedReview.approverGroups };
                    }
                    return currentReview;
                });
            })
        );
    }

    public updateIncidentReviewGroupApproverStatusById(approverGroup: IncidentReportApproverGroupModel) {
        return this.httpClient.put<IncidentReviewModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/approver/group/employee/status`, approverGroup
        ).pipe(
            tap((updatedReview: IncidentReviewModel) => {
                this.incidentReportReviewSourceSignal.set(updatedReview)
            })
        );
    }

    public updateIncidentReviewsForApproverId(incidentReportId: number, employeeId: number) {
        return this.httpClient.put<IncidentReviewModel[]>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/${incidentReportId}/reviews/employees/${employeeId}/approve-all`, 
            {}
        );
    }

    public updateIncidentReviewApproverStatusById(approverId: number, approver: IncidentReviewApproverModel) {
        return this.httpClient.put<IncidentReviewApproverModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/approver/${approverId}/status`, approver
        ).pipe(
            tap((updatedApprover: IncidentReviewApproverModel) => {
                // Update the incidentReportReviewSourceSignal
                this.incidentReportReviewSourceSignal.update(currentReview => {
                    if (currentReview && currentReview.approvers) {
                        return { ...currentReview, approvers: this.updateApprovers(currentReview.approvers, updatedApprover) };
                    }
                    return currentReview;
                });

                // Update array of narrative reviews
                if (this.incidentReportNarrativeReviewsSourceSignal().length > 0) {
                    this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                        return currentReviews.map(currentReview => {
                            if (currentReview && currentReview.approvers) {
                                return { ...currentReview, approvers: this.updateApprovers(currentReview.approvers, updatedApprover) };
                            }
                            return currentReview;
                        });
                    });
                }

                // Update the active narrative signal's review object inside the narrative
                this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
                    if (currentNarrative?.review) {
                        const currentReview = currentNarrative.review;
                        return {
                            ...currentNarrative,
                            review: {
                                ...currentReview,
                                approvers: this.updateApprovers(currentReview.approvers, updatedApprover)
                            }
                        };
                    }
                    return currentNarrative;
                });
            })
        );
    }
    private updateApprovers(approvers: IncidentReviewApproverModel[], updatedApprover: IncidentReviewApproverModel): IncidentReviewApproverModel[] {
        return approvers.map(approver => approver.id === updatedApprover.id ? updatedApprover : approver);
    }


    public createIncidentReview(review: IncidentReviewModel) {
        return this.httpClient.post<IncidentReviewModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/incidents/review`
            ), review
        ).pipe(
            tap((createdReview: IncidentReviewModel) => {
                this.incidentReportReviewSourceSignal.set(createdReview)
            })
        )
    }

    public updateIncidentReportReviewForResubmission(review: IncidentReviewModel) {
        return this.httpClient.put<IncidentReviewModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/incidents/review`
            ), review
        ).pipe(
            tap((createdReview: IncidentReviewModel) => {
                this.incidentReportReviewSourceSignal.set(createdReview)
            })
        )
    }

    public createIncidentNarrativeReview(review: IncidentReviewModel) {
        return this.httpClient.post<IncidentReviewModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/incidents/narrative/review`
            ), review
        ).pipe(
            tap((createdNarrativeReview: IncidentReviewModel) => {
                this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                    return [...currentReviews, createdNarrativeReview];
                });

            })
        )

    }

    public updateIncidentReviewStatusById(reviewId: number, statusId: IncidentReviewStatus) {
        return this.httpClient.put<IncidentReviewModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/${reviewId}/status`, statusId
        ).pipe(
            tap((updatedReview: IncidentReviewModel) => {
                this.updateReviewStatus(updatedReview);
            })
        );
    }

    public updateIncidentReviewStatusToUnapprove(reviewId: number) {
        return this.httpClient.put<IncidentReviewModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/${reviewId}/status/unapprove`, {}
        ).pipe(
            tap((updatedReview: IncidentReviewModel) => {
                this.updateReviewStatus(updatedReview);
            })
        );
    }

    public updateReviewStatus(updatedReview: IncidentReviewModel) {
        // Update the main review signal
        this.incidentReportReviewSourceSignal.set(updatedReview);

        // Update array of narrative reviews
        if (this.incidentReportNarrativeReviewsSourceSignal().length > 0) {
            this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                return currentReviews.map(currentReview =>
                    currentReview.id === updatedReview.id ? updatedReview : currentReview
                );
            });
        }

        // Update the active narrative signal's review object inside the narrative
        this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
            if (currentNarrative?.review && currentNarrative?.review?.id === updatedReview.id) {
                return { ...currentNarrative, review: updatedReview };
            }
            return currentNarrative;
        });
    }

    public updateIncidentReviewCommentStatus(incidentReportReviewComment: IncidentReviewCommentModel) {
        return this.httpClient.put<IncidentReviewCommentModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/comment/${incidentReportReviewComment?.id}/status`, incidentReportReviewComment
        ).pipe(
            tap((response) => {
                // Update individual signal
                this.incidentReportReviewSourceSignal.update(currentReview => this.updateReviewCommentStatus(currentReview!, response));

                // Update array of narrative reviews
                if (this.incidentReportNarrativeReviewsSourceSignal().length > 0) {
                    this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                        return currentReviews.map(currentReview => {
                            if (currentReview && currentReview.incidentReportReviewComments) {
                                return this.updateReviewCommentStatus(currentReview, response);
                            }
                            return currentReview;
                        });
                    });
                }

                // Update the active narrative signal's review object inside the narrative
                this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
                    if (currentNarrative?.review) {
                        return { ...currentNarrative, review: this.updateReviewCommentStatus(currentNarrative.review, response) };
                    }
                    return currentNarrative;
                });
            })
        );
    }

    // Function to update comment status within a review
    private updateReviewCommentStatus(review: IncidentReviewModel, response: IncidentReviewCommentModel): IncidentReviewModel {
        if (review && review.incidentReportReviewComments) {
            const updatedComments = review.incidentReportReviewComments.map(comment => {
                if (comment.id === response.id) {
                    return { ...comment, incidentReviewCommentStatusId: response.incidentReviewCommentStatusId };
                }
                return comment;
            });
            return { ...review, incidentReportReviewComments: updatedComments };
        }
        return review;
    }

    public resolveIncidentReviewCommentStatuses(incidentReportReviewComments: IncidentReviewCommentModel[]) {
        return this.httpClient.put<IncidentReviewCommentModel[]>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/incidents/review/comments/status`, incidentReportReviewComments
        ).pipe(
            tap((responses) => {
                // Update individual signal
                this.incidentReportReviewSourceSignal.update(currentReview => this.updateReviewCommentStatuses(currentReview!, responses));

                // Update array of narrative reviews
                this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                    if (currentReviews && currentReviews.length > 0) {
                        return currentReviews.map(currentReview => this.updateReviewCommentStatuses(currentReview, responses));
                    }
                    return currentReviews;
                });

                // Update nested object within the active narrative signal
                this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
                    if (currentNarrative?.review) {
                        return { ...currentNarrative, review: this.updateReviewCommentStatuses(currentNarrative.review, responses) };
                    }
                    return currentNarrative;
                });
            })
        );
    }

    // Function to update comment statuses within a review
    private updateReviewCommentStatuses(review: IncidentReviewModel, responses: IncidentReviewCommentModel[]): IncidentReviewModel {
        if (review && review.incidentReportReviewComments) {
            const updatedComments = review.incidentReportReviewComments.map(existingComment => {
                const updatedComment = responses.find(response => response.id === existingComment.id);
                if (updatedComment) {
                    return { ...existingComment, incidentReviewCommentStatusId: updatedComment.incidentReviewCommentStatusId };
                }
                return existingComment;
            });
            return { ...review, incidentReportReviewComments: updatedComments };
        }
        return review;
    }

    public createIncidentReviewComment(incidentReportReviewComment: IncidentReviewCommentModel) {
        return this.httpClient.post<IncidentReviewCommentModel>(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/incidents/review/${incidentReportReviewComment.incidentReportReviewId}/comment`
            ), incidentReportReviewComment
        ).pipe(
            tap((response: IncidentReviewCommentModel) => {
                const currentReview = this.incidentReportReviewSourceSignal();
                if (currentReview && currentReview.incidentReportReviewComments) {
                    this.incidentReportReviewSourceSignal.set(this.addCommentToReview(currentReview, response));
                }

                // Update array of narrative reviews
                if (this.incidentReportNarrativeReviewsSourceSignal().length > 0) {
                    this.incidentReportNarrativeReviewsSourceSignal.update(currentReviews => {
                        return currentReviews.map(currentReview => {
                            if (
                                currentReview && 
                                currentReview.id === response.incidentReportReviewId && 
                                currentReview.incidentReportReviewComments
                            ) {
                                return this.addCommentToReview(currentReview, response);
                            }
                            return currentReview;
                        });
                    });
                }

                // Update the active narrative signal's review object inside the narrative object
                this.incidentReportActiveNarrativeSignal?.update(currentNarrative => {
                    if (currentNarrative?.review) {
                        return { ...currentNarrative, review: this.addCommentToReview(currentNarrative.review, response) };
                    }
                    return currentNarrative;
                });
            })
        )
    }

    // Function to add a new comment to a review
    private addCommentToReview(review: IncidentReviewModel, response: IncidentReviewCommentModel): IncidentReviewModel {
        if (review && review.incidentReportReviewComments) {
            const updatedComments = [...review.incidentReportReviewComments, response];
            return { ...review, incidentReportReviewComments: updatedComments };
        }
        return review;
    }

    public setIsComingFromOutsideModule(isComingFromOutsideModule: boolean, navigateTo: string) {
        this.isComingFromOutsideModuleSourceSignal.set(isComingFromOutsideModule);
        this.navigateDirectlyToRouteSourceSignal.set(navigateTo);
    }

    //#endregion reviews

    public getAboutInformation(incidentId: number) {
        return this.httpClient
            .get<AboutEntityModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/about`)
            );
    }

    public createOrganizationIncident(organizationIncident: OrganizationIncidentModel) {
        return this.httpClient.post<OrganizationIncidentModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/organization-incident-report?organizationId=${organizationIncident.organizationId}&incidentReportId=${organizationIncident.incidentReportId}`, organizationIncident
        ).pipe(
            catchError(() => {
                this.toastMessageService.showError(`An error occurred creating an incident association for organization with id ${this.incidentIdComputedSignal()}.`);
                return EMPTY;
            }),
            tap((response: OrganizationIncidentModel) => {
                this.incidentOrganizationsSourceSignal.update(organizationIncidents => [...organizationIncidents, response]);
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationOrganizationAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                ));
            })
        );
    }

    public updateOrganizationIncident(organizationId: number, incidentId: number, organizationIncident: OrganizationIncidentModel) {
        return this.httpClient.put<OrganizationIncidentModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/organization-incident-report?organizationId=${organizationId}&incidentReportId=${incidentId}`),
            organizationIncident
        ).pipe(
            catchError(() => {
                this.toastMessageService.showError(`An error occurred updating an organization association for incident with id ${incidentId}.`);
                return EMPTY;
            }),
            tap((response: OrganizationIncidentModel) => {
                this.incidentOrganizationsSourceSignal.update(organizationIncidents => organizationIncidents.map(organizationIncident => organizationIncident.id === response.id ? response : organizationIncident));
            }),
        );
    }

    public setIncidentOrganizationPopoutForm() {
        return forkJoin([
            of(incidentOrganizationFields.fields),
            of(incidentOrganizationFields.customFields)
        ]).pipe(
            map(([incidentOrganizationFields, incidentOrganizationCustomFields]) => ({ incidentOrganizationFields, incidentOrganizationCustomFields }))
        );
    }

    public getOrganizationIncidents(incidentReportId: number) {
        return this.httpClient.get<OrganizationIncidentModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/organization-incident-report?incidentReportId=${incidentReportId}`)
            .pipe(
                catchError(() => {
                    this.incidentOrganizationsSourceSignal.set([]);
                    this.toastMessageService.showError(`An error occurred retrieving organization associations for incident with id ${incidentReportId}.`);
                    return EMPTY;
                }),
                tap((response: OrganizationIncidentModel[]) => {
                    this.incidentOrganizationsSourceSignal.set(response);

                })
            );
    }

    public deleteOrganizationIncidents(incidentReportId: number, organizationIncidentIds: number[]): Observable<any> {
        return this.httpClient.delete<void>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/organization-incident-report`, { body: organizationIncidentIds })
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred deleting incident associations for organization with id ${incidentReportId}.`);
                    return EMPTY;
                }),
                tap(() => {
                    this.incidentOrganizationsSourceSignal.update(organizationIncidents => organizationIncidents.filter(organizationIncident => !organizationIncidentIds.includes(organizationIncident.id!)));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationOrganizationAndIncidentReportType ? { ...c, count: c.count - organizationIncidentIds.length } : c
                    ));
                }),
                map(() => true)
            );
    }

    public getIncidentActivities(incidentReportId: number): Observable<OfficerActivityIncidentModel[]> {
        return this.httpClient.get<OfficerActivityIncidentModel[]>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/incident-activities/${incidentReportId}`
        ).pipe(
            tap(incidentActivities => this.incidentActivitiesSourceSignal.set(incidentActivities))
        );
    }

    public setIncidentChargingAssociationForm() {
        return forkJoin([
            of(incidentChargingFields.fields),
            of(incidentChargingFields.customFields)
        ]).pipe(
            map(([incidentChargingFields, incidentChargingCustomFields]) => ({ incidentChargingFields, incidentChargingCustomFields }))
        );
    }

    public saveChargingAssociation(assoc: ChargingIncidentModel) {
        return this.httpClient
            .post<ChargingIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-incidents`), assoc
            ).pipe(
                tap((response: ChargingIncidentModel) => {
                    this.incidentChargingsSourceSignal.update(chargingIncidents => [...chargingIncidents, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationChargingAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
            );
    }

    public updateChargingAssociation(assoc: ChargingIncidentModel) {
        return this.httpClient
            .put<ChargingIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/charging-incidents/${assoc.id}`
                ),
                assoc
            )
            .pipe(
                tap((response: ChargingIncidentModel) => {
                    this.incidentChargingsSourceSignal.update(chargingIncidents => chargingIncidents.map(chargingIncident => chargingIncident.id === response.id ? response : chargingIncident));
                }),
            );
    }

    public getChargingAssociations(nrfId: number) {
        // will update model once its solidified
        return this.httpClient
            .get<ChargingIncidentModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/charging-incidents?incidentReportId=${nrfId}`
                )
            ).pipe(
                tap((chargings) => {
                    // Populate the signal with the fetched data
                    this.incidentChargingsSourceSignal.set(chargings);
                }),
                catchError(() => {
                    // Handle the error by setting an empty array to indicate no data or an error state
                    this.incidentChargingsSourceSignal.set([]);
                    // Return an observable with an empty array to continue the observable chain
                    return EMPTY;
                })
            );
    }

    //TODO: update delete to delete multiple records in a single api call
    public deleteChargingAssociation(chargingAndIncidentReportId: number): any {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(
                `/api/v1/association/charging-incidents/${chargingAndIncidentReportId}`
            )
        ).pipe(
            tap(() => {
                // On successful delete, update the signal source by removing the deleted item
                this.incidentChargingsSourceSignal.update(chargingIncidents => chargingIncidents.filter(chargingIncident => chargingIncident.id !== chargingAndIncidentReportId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationChargingAndIncidentReportType ? { ...c, count: c.count - 1 } : c
                ));
            }),
        );
    }

    public setPropertyIncidentForm() {
        return forkJoin([
            of(incidentPropertyFields.fields),
            of(incidentPropertyFields.customFields)
        ]).pipe(
            map(([incidentPropertyFields, incidentPropertyCustomFields]) => ({ incidentPropertyFields, incidentPropertyCustomFields }))
        );
    }

    public createPropertyIncident(propertyIncident: PropertyIncidentModel) {
        return this.httpClient.post<PropertyIncidentModel>(
            `${this.environmentService.pathfinderApiUrl}/api/v1/association/property-incident-report?propertyId=${propertyIncident.propertyId}&incidentReportId=${propertyIncident.incidentReportId}`, propertyIncident
        ).pipe(
            catchError(() => {
                this.toastMessageService.showError(`An error occurred creating an property association for incident report with id ${this.incidentIdSourceSignal()}.`);
                return EMPTY;
            }),
            tap((response: PropertyIncidentModel) => {
                this.incidentPropertiesSourceSignal.update(propertyIncidents => [...propertyIncidents, response]);
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationPropertyAndIncidentReportType ? { ...c, count: c.count + 1 } : c
                ));
            })
        );
    }

    public updatePropertyIncident(propertyId: number, incidentReportId: number, propertyIncident: PropertyIncidentModel) {
        return this.httpClient.put<PropertyIncidentModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/property-incident-report?propertyId=${propertyId}&incidentReportId=${incidentReportId}`),
            propertyIncident
        ).pipe(
            catchError(() => {
                this.toastMessageService.showError(`An error occurred updating an property association for incident report with id ${incidentReportId}.`);
                return EMPTY;
            }),
            tap((response: PropertyIncidentModel) => {
                this.incidentPropertiesSourceSignal.update(propertyIncidents => propertyIncidents.map(propertyIncident => propertyIncident.id === response.id ? response : propertyIncident));
            })
        );
    }

    public getPropertyIncidents(incidentReportId: number) {
        return this.httpClient.get<PropertyIncidentModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/property-incident-report?incidentReportId=${incidentReportId}`)
            .pipe(
                catchError(() => {
                    this.incidentPropertiesSourceSignal.set([]);
                    this.toastMessageService.showError(`An error occurred retrieving property associations for incident report with id ${incidentReportId}.`);
                    return EMPTY;
                }),
                switchMap((propertyResponse: PropertyIncidentModel[]) => {
                    const allUserIds = propertyResponse.map(propAndIncident => propAndIncident.property?.createdByUserId || 0);
                    return this.authService.getUsersFirstAndLastNamesByIds(allUserIds).pipe(
                        map((userResponse) => {
                            return propertyResponse.map(propertyIncident => {
                                return propertyIncident.property ? this.mergePropertyAndUser(propertyIncident, userResponse[propertyIncident.property.createdByUserId]) : propertyIncident
                            })
                        }),
                        catchError(() => {
                            return of(propertyResponse)
                        }),
                    )
                }),
                tap((response: PropertyIncidentModel[]) => {
                    this.incidentPropertiesSourceSignal.set(response);
                })
            );
    }

    public mergePropertyAndUser(propertyIncident: PropertyIncidentModel, name: string) {
        const mergedModel: PropertyIncidentModel = {
            ...propertyIncident,
            property: propertyIncident.property ? {
                ...propertyIncident.property!,
                createdByUser: name,
                createdOn: propertyIncident.property?.createdOn ? new Date(propertyIncident.property?.createdOn) : undefined
            } : undefined
        }
        return mergedModel;
    }

    public deletePropertyIncidents(incidentReportId: number, propertyIncidentIds: number[]): Observable<void> {
        return this.httpClient.delete<void>(`${this.environmentService.pathfinderApiUrl}/api/v1/association/property-incident-report`, { body: propertyIncidentIds })
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred deleting property associations for incident report with id ${incidentReportId}.`);
                    return EMPTY;
                }),
                tap(() => {
                    this.incidentPropertiesSourceSignal.update(propertyIncidents => propertyIncidents.filter(organizationIncident => !propertyIncidentIds.includes(organizationIncident.id!)));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationPropertyAndIncidentReportType ? { ...c, count: c.count - propertyIncidentIds.length } : c
                    ));
                }),
            );
    }

    //#region NIBRS
    public getIncidentNibrsSubmissionId(incidentReportId: number) {
        return this.httpClient
            .get<number>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/submission/incidents/${incidentReportId}`
                )
            );
    }

    public setNibrsPrimaryInformationForm() {
        return forkJoin([
            of(nibrsPrimaryInformationFields.fields),
            of(nibrsPrimaryInformationFields.customFields)
        ]).pipe(
            map(([nibrsPrimaryInfoForm, nibrsPrimaryInfoCustomFields]) => ({ nibrsPrimaryInfoForm, nibrsPrimaryInfoCustomFields }))
        );
    }

    public getIncidentNibrsPrimaryInfo(incidentReportId: number) {
        return this.httpClient
            .get<NibrsPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/primary-info/incidents/${incidentReportId}`
                )
            )
            .pipe(
                tap((result) => this.incidentNibrsPrimaryInformationSourceSignal.set(result))
            );
    }

    public updateIncidentNibrsPrimaryInfo(primaryInfo: NibrsPrimaryInformationModel) {
        return this.httpClient
            .put<NibrsPrimaryInformationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/primary-info`
                ),
                primaryInfo
            )
            .pipe(
                tap((result) => this.incidentNibrsPrimaryInformationSourceSignal.set(result))
            );
    }

    public syncReviewApproverToNibrs(approverId: number) {
        return this.httpClient
            .put(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/primary-info/approver/${approverId}/sync`
                ), null
            )
    }

    public setIncidentActiveNibrsVictim(victim: NibrsVictimModel | null): void {
        this.incidentActiveNibrsVictimSourceSignal.set(victim);
    }

    public setNibrsVictimPopoutForm() {
        return forkJoin([
            of(nibrsVictimPopoutFormFields.fields),
            of(nibrsVictimPopoutFormFields.customFields)
        ]).pipe(
            map(([nibrsVictimPopoutFields, nibrsVictimPopoutCustomFields]) => ({ nibrsVictimPopoutFields, nibrsVictimPopoutCustomFields }))
        );
    }

    public getIncidentNibrsVictims(incidentId: number): Observable<NibrsVictimModel[]> {
        return this.httpClient.get<NibrsVictimModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/victims/incidents/${incidentId}`)
        )
            .pipe(
                tap((victims: NibrsVictimModel[]) => {
                    this.incidentNibrsVictimsSourceSignal.set(victims);
                }),
                catchError(() => {
                    this.incidentNibrsVictimsSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentNibrsVictim(victim: NibrsVictimModel) {
        return this.httpClient
            .post<NibrsVictimModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/victims`), victim
            ).pipe(
                tap((response: NibrsVictimModel) => {
                    this.incidentNibrsVictimsSourceSignal.update(victims => [...victims, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndNIBRSVictimType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentNibrsVictim(victim: NibrsVictimModel) {
        return this.httpClient
            .put<NibrsVictimModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/victims/${victim.id}`
                ),
                victim
            )
            .pipe(
                tap((response: NibrsVictimModel) => {
                    this.incidentNibrsVictimsSourceSignal.update(victims => victims.map(v => v.id === response.id ? response : v));
                }),
            );
    }

    public deleteIncidentNibrsVictim(victimId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/victims/${victimId}`),
        ).pipe(
            tap(() => {
                this.incidentNibrsVictimsSourceSignal.update(victims => victims.filter(victim => victim.id !== victimId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNIBRSVictimType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setIncidentActiveNibrsOffender(offender: NibrsOffenderModel | null): void {
        this.incidentActiveNibrsOffenderSourceSignal.set(offender);
    }

    public setNibrsOffenderPopoutForm() {
        return forkJoin([
            of(nibrsOffenderPopoutFormFields.fields),
            of(nibrsOffenderPopoutFormFields.customFields)
        ]).pipe(
            map(([nibrsOffenderPopoutFields, nibrsOffenderPopoutCustomFields]) => ({ nibrsOffenderPopoutFields, nibrsOffenderPopoutCustomFields }))
        );
    }

    public getIncidentNibrsOffenders(incidentId: number): Observable<NibrsOffenderModel[]> {
        return this.httpClient.get<NibrsOffenderModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/offenders/incidents/${incidentId}`)
        )
            .pipe(
                tap((offenders: NibrsOffenderModel[]) => {
                    this.incidentNibrsOffendersSourceSignal.set(offenders);
                }),
                catchError(() => {
                    this.incidentNibrsOffendersSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentNibrsOffender(offender: NibrsOffenderModel) {
        return this.httpClient
            .post<NibrsOffenderModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/offenders`), offender
            ).pipe(
                tap((response: NibrsOffenderModel) => {
                    this.incidentNibrsOffendersSourceSignal.update(offenders => [...offenders, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndNIBRSOffenderType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentNibrsOffender(offender: NibrsOffenderModel) {
        return this.httpClient
            .put<NibrsOffenderModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/offenders/${offender.id}`
                ),
                offender
            )
            .pipe(
                tap((response: NibrsOffenderModel) => {
                    this.incidentNibrsOffendersSourceSignal.update(offenders => offenders.map(o => o.id === response.id ? response : o));
                }),
            );
    }

    public deleteIncidentNibrsOffender(offenderId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/offenders/${offenderId}`),
        ).pipe(
            tap(() => {
                this.incidentNibrsOffendersSourceSignal.update(offenders => offenders.filter(offender => offender.id !== offenderId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNIBRSOffenderType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setNibrsOffensePopoutForm() {
        return forkJoin([
            of(nibrsOffensePopoutFormFields.fields),
            of(nibrsOffensePopoutFormFields.customFields)
        ]).pipe(
            map(([nibrsOffensePopoutFields, nibrsOffensePopoutCustomFields]) => ({ nibrsOffensePopoutFields, nibrsOffensePopoutCustomFields }))
        );
    }

    public setIncidentActiveNibrsProperty(property: NibrsPropertyModel | null): void {
        this.incidentActiveNibrsPropertySourceSignal.set(property);
    }

    public setNibrsPropertyPopoutForm() {
        return forkJoin([
            of(nibrsPropertyPopoutFormFields.fields),
            of(nibrsPropertyPopoutFormFields.customFields)
        ]).pipe(
            map(([nibrsPropertyPopoutFields, nibrsPropertyPopoutCustomFields]) => ({ nibrsPropertyPopoutFields, nibrsPropertyPopoutCustomFields }))
        );
    }

    public getIncidentNibrsProperty(incidentId: number): Observable<NibrsPropertyModel[]> {
        return this.httpClient.get<NibrsPropertyModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/property-details/incidents/${incidentId}`)
        )
            .pipe(
                tap((property: NibrsPropertyModel[]) => {
                    this.incidentNibrsPropertiesSourceSignal.set(property);
                }),
                catchError(() => {
                    this.incidentNibrsPropertiesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentNibrsProperty(property: NibrsPropertyModel) {
        return this.httpClient
            .post<NibrsPropertyModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/property-details`), property
            ).pipe(
                tap((response: NibrsPropertyModel) => {
                    this.incidentNibrsPropertiesSourceSignal.update(property => [...property, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndNIBRSPropertyDetailType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentNibrsProperty(property: NibrsPropertyModel) {
        return this.httpClient
            .put<NibrsPropertyModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/property-details/${property.id}`
                ),
                property
            )
            .pipe(
                tap((response: NibrsPropertyModel) => {
                    this.incidentNibrsPropertiesSourceSignal.update(property => property.map(p => p.id === response.id ? response : p));
                }),
            );
    }

    public deleteIncidentNibrsProperty(propertyId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/property-details/${propertyId}`),
        ).pipe(
            tap(() => {
                this.incidentNibrsPropertiesSourceSignal.update(property => property.filter(property => property.id !== propertyId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNIBRSPropertyDetailType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setIncidentActiveNibrsArrestee(arrestee: NibrsArresteeDetailModel | null): void {
        this.incidentActiveNibrsArresteeDetailSourceSignal.set(arrestee);
    }

    public setNibrsArresteePopoutForm() {
        return forkJoin([
            of(nibrsArresteePopoutFormFields.fields),
            of(nibrsArresteePopoutFormFields.customFields)
        ]).pipe(
            map(([nibrsArresteePopoutFields, nibrsArresteePopoutCustomFields]) => ({ nibrsArresteePopoutFields, nibrsArresteePopoutCustomFields }))
        );
    }

    public getIncidentNibrsArrestee(incidentId: number): Observable<ChargingIncidentModel[]> {
        return this.httpClient.get<ChargingIncidentModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/arrestee/incidents/${incidentId}`)
        )
            .pipe(
                tap((arrestees: ChargingIncidentModel[]) => {
                    this.incidentNibrsArresteesSourceSignal.set(arrestees);
                }),
                catchError(() => {
                    this.incidentNibrsArresteesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentNibrsArrestee(arrestee: NibrsArresteeDetailModel) {
        return this.httpClient
            .post<ChargingIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/arrestee`), arrestee
            ).pipe(
                tap((response: ChargingIncidentModel) => {
                    this.incidentNibrsArresteesSourceSignal.update(arrestees => [...arrestees, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndNIBRSArresteeDetailType ? { ...c, count: c.count + 1 } : c
                    ));
                }),
            );
    }

    public updateIncidentNibrsArrestee(arrestee: NibrsArresteeDetailModel) {
        return this.httpClient
            .put<ChargingIncidentModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/nibrs/arrestee/${arrestee.id}`
                ),
                arrestee
            )
            .pipe( //reload full list because charging link may have been changed
                switchMap((response) => {
                    return this.getIncidentNibrsArrestee(this.incidentIdComputedSignal())
                        .pipe(map((_ => response)))
                })
            );
    }

    public deleteIncidentNibrsArrestee(arresteeId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/nibrs/arrestee/${arresteeId}`),
        ).pipe(
            tap(() => {
                this.incidentNibrsArresteesSourceSignal.update(arrestees => arrestees.filter(arrestee => arrestee.nibrsArresteeDetailId !== arresteeId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndNIBRSArresteeDetailType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }
    //#endregion

    //#region UCR
    public getIncidentUcrMainInfo(incidentReportId: number) {
        return this.httpClient
            .get<IncidentUcrDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/main-info/incidents/${incidentReportId}`
                )
            )
            .pipe(
                tap((result) => this.incidentUcrMainInformationSourceSignal.set(result))
            );
    }

    public updateIncidentUcrMainInfo(mainInfo: IncidentUcrDetailModel) {
        return this.httpClient
            .put<IncidentUcrDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/main-info`
                ),
                mainInfo
            )
            .pipe(
                tap((result) => this.incidentUcrMainInformationSourceSignal.set(result))
            );
    }

    public setUcrPersonForm() {
        return forkJoin([
            of(ucrPersonFields.fields),
            of(ucrPersonFields.customFields)
        ]).pipe(
            map(([ucrPersonForm, ucrPersonCustomFields]) => ({ ucrPersonForm, ucrPersonCustomFields }))
        );
    }

    public getIncidentUcrPersons(incidentReportId: number) {
        return this.httpClient
            .get<UcrPersonModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/persons/incidents/${incidentReportId}`
                )
            ).pipe(
                tap((persons: UcrPersonModel[]) => {
                    this.incidentUcrPersonsSourceSignal.set(persons);
                }),
                catchError(() => {
                    this.incidentUcrPersonsSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public updateIncidentUcrPersons(incidentReportId: number, ucrPersons: UcrPersonModel[]) {
        return this.httpClient
            .put<UcrPersonModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/persons/incidents/${incidentReportId}`
                ),
                ucrPersons
            )
            .pipe(
                tap((response: UcrPersonModel[]) => {
                    this.incidentUcrPersonsSourceSignal.set(response);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.UcrPersonType ? { ...c, count: response.length } : c
                    ));
                })
            );
    }

    public setUcrLeokaForm() {
        return forkJoin([
            of(ucrLeokaFields.fields),
            of(ucrLeokaFields.customFields)
        ]).pipe(
            map(([ucrLeokaForm, ucrLeokaCustomFields]) => ({ ucrLeokaForm, ucrLeokaCustomFields }))
        );
    }

    public getIncidentUcrLeokaInfo(incidentReportId: number) {
        return this.httpClient
            .get<IncidentLeokaDetailModel | null>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/leoka/incidents/${incidentReportId}`
                )
            )
    }

    public createIncidentUcrLeokaInfo(leokaInfo: IncidentLeokaDetailModel) {
        return this.httpClient
            .post<IncidentLeokaDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/leoka`
                ),
                leokaInfo
            ).pipe(
                tap((response: IncidentLeokaDetailModel) => {
                    this.incidentUcrLeokaDetailModelSourceSignal.set(response);
                })
            );
    }

    public updateIncidentUcrLeokaInfo(leokaInfo: IncidentLeokaDetailModel) {
        return this.httpClient
            .put<IncidentLeokaDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/leoka`
                ),
                leokaInfo
            ).pipe(
                tap((response: IncidentLeokaDetailModel) => {
                    this.incidentUcrLeokaDetailModelSourceSignal.set(response);
                })
            );
    }

    public deleteIncidentUcrLeokaInfoByIncidentReportId(incidentReportId: number) {
        return this.httpClient
            .delete(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/leoka/${incidentReportId}`
                )
            ).pipe(
                tap(() => {
                    this.incidentUcrLeokaDetailModelSourceSignal.set(null);
                })
            );
    }

    public canDeleteIncidentUcrLeokaInfo(personAndIncidentReportIds: number[]) {
        if (!personAndIncidentReportIds || personAndIncidentReportIds.length === 0 || !this.incidentUcrLeokaDetailModelSourceSignal()) {
            return false;
        }

        const ucrLeokaPersons = this.incidentUcrLeokaPersonsComputed();
        const hasMatch = ucrLeokaPersons.some(person =>
            !personAndIncidentReportIds.includes(person.personAndIncidentReportId || 0)
        );

        return (ucrLeokaPersons.length > 0 && !hasMatch);
    }

    public setUcrHomicideForm() {
        return forkJoin([
            of(ucrHomicideFields.fields),
            of(ucrHomicideFields.customFields)
        ]).pipe(
            map(([ucrHomicideForm, ucrHomicideCustomFields]) => ({ ucrHomicideForm, ucrHomicideCustomFields }))
        );
    }

    public getIncidentUcrHomicideInfo(incidentReportId: number) {
        return this.httpClient
            .get<UcrHomicideMasterModel | null>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/homicide/incidents/${incidentReportId}`
                )
            )
            .pipe(
                tap((result) => this.incidentUcrHomicideInformationSourceSignal.set(result))
            );
    }

    public createIncidentUcrHomicideInfo(homicideInfo: UcrHomicideMasterModel) {
        return this.httpClient
            .post<UcrHomicideMasterModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/homicide`
                ),
                homicideInfo
            );
    }

    public updateIncidentUcrHomicideInfo(homicideInfo: UcrHomicideMasterModel) {
        return this.httpClient
            .put<UcrHomicideMasterModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/homicide`
                ),
                homicideInfo
            );
    }

    public setIncidentActiveUcrProperty(property: UcrPropertyModel | null): void {
        this.incidentActiveUcrPropertySourceSignal.set(property);
    }

    public setUcrPropertyPopoutForm() {
        return forkJoin([
            of(ucrPropertyPopoutFormFields.fields),
            of(ucrPropertyPopoutFormFields.customFields)
        ]).pipe(
            map(([ucrPropertyPopoutFields, ucrPropertyPopoutCustomFields]) => ({ ucrPropertyPopoutFields, ucrPropertyPopoutCustomFields }))
        );
    }

    public getIncidentUcrProperty(incidentId: number): Observable<UcrPropertyModel[]> {
        return this.httpClient.get<UcrPropertyModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/property-details/incidents/${incidentId}`)
        )
            .pipe(
                tap((property: UcrPropertyModel[]) => {
                    this.incidentUcrPropertiesSourceSignal.set(property);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c => 
                        c.associationCountType === CountType.AssociationIncidentReportAndUCRPropertyDetailType ? { ...c, count: property.length } : c
                    ));
                }),
                catchError(() => {
                    this.incidentUcrPropertiesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentUcrProperty(property: UcrPropertyModel) {
        return this.httpClient
            .post<UcrPropertyModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/property-details`), property
            ).pipe(
                tap((response: UcrPropertyModel) => {
                    this.incidentUcrPropertiesSourceSignal.update(property => [...property, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndUCRPropertyDetailType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentUcrProperty(property: UcrPropertyModel) {
        return this.httpClient
            .put<UcrPropertyModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/property-details/${property.id}`
                ),
                property
            )
            .pipe(
                tap((response: UcrPropertyModel) => {
                    this.incidentUcrPropertiesSourceSignal.update(property => property.map(p => p.id === response.id ? response : p));
                }),
            );
    }

    public deleteIncidentUcrProperty(propertyId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/property-details/${propertyId}`),
        ).pipe(
            tap(() => {
                this.incidentUcrPropertiesSourceSignal.update(property => property.filter(property => property.id !== propertyId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndUCRPropertyDetailType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setIncidentActiveUcrArrestee(arrestee: IncidentArrestUcrDetailModel | null): void {
        this.incidentActiveUcrArresteeSourceSignal.set(arrestee);
    }

    public setUcrArresteePopoutForm() {
        return forkJoin([
            of(ucrArresteePopoutFormFields.fields),
            of(ucrArresteePopoutFormFields.customFields)
        ]).pipe(
            map(([ucrArresteePopoutFields, ucrArresteePopoutCustomFields]) => ({ ucrArresteePopoutFields, ucrArresteePopoutCustomFields }))
        );
    }

    public getIncidentUcrArrestee(incidentId: number): Observable<IncidentArrestUcrDetailModel[]> {
        return this.httpClient.get<IncidentArrestUcrDetailModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/arrestee/incidents/${incidentId}`)
        )
            .pipe(
                tap((arrestee: IncidentArrestUcrDetailModel[]) => {
                    this.incidentUcrArresteesSourceSignal.set(arrestee);
                }),
                catchError(() => {
                    this.incidentUcrArresteesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public createIncidentUcrArrestee(arrestee: IncidentArrestUcrDetailModel) {
        return this.httpClient
            .post<IncidentArrestUcrDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/arrestee`), arrestee
            ).pipe(
                tap((response: IncidentArrestUcrDetailModel) => {
                    this.incidentUcrArresteesSourceSignal.update(arrestee => [...arrestee, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndUCRArresteeDetailType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentUcrArrestee(arrestee: IncidentArrestUcrDetailModel) {
        return this.httpClient
            .put<IncidentArrestUcrDetailModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/ucr/arrestee/${arrestee.id}`
                ),
                arrestee
            )
            .pipe(
                tap((response: IncidentArrestUcrDetailModel) => {
                    this.incidentUcrArresteesSourceSignal.update(arrestee => arrestee.map(p => p.id === response.id ? response : p));
                }),
            );
    }

    public deleteIncidentUcrArrestee(arresteeId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/ucr/arrestee/${arresteeId}`),
        ).pipe(
            tap(() => {
                this.incidentUcrArresteesSourceSignal.update(arrestee => arrestee.filter(arrestee => arrestee.id !== arresteeId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.AssociationIncidentReportAndUCRArresteeDetailType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }
    //#endregion

    //#region Clery
    public getIncidentCleryIncidentId(incidentReportId: number) {
        return this.httpClient
            .get<number>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/incidents/${incidentReportId}`
                )
            );
    }

    public setCleryPrimaryInfoForm() {
        return forkJoin([
            of(cleryPrimaryInfoFormFields.fields),
            of(cleryPrimaryInfoFormFields.customFields)
        ]).pipe(
            map(([cleryPrimaryInfoForm, cleryPrimaryInfoCustomFields]) => ({ cleryPrimaryInfoForm, cleryPrimaryInfoCustomFields }))
        );
    }

    public getIncidentCleryPrimaryInfo(incidentReportId: number) {
        return this.httpClient
            .get<CleryPrimaryInfoModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/primary-info/incidents/${incidentReportId}`
                )
            );
    }

    public updateIncidentCleryPrimaryInfo(cleryPrimaryInfo: CleryPrimaryInfoModel) {
        return this.httpClient
            .put<CleryPrimaryInfoModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/primary-info`
                ),
                cleryPrimaryInfo
            );
    }

    public setIncidentActiveCleryPerson(person: CleryPersonModel | null): void {
        this.incidentActiveCleryPersonSourceSignal.set(person);
    }

    public setCleryPersonPopoutForm() {
        return forkJoin([
            of(cleryPersonPopoutFormFields.fields),
            of(cleryPersonPopoutFormFields.customFields)
        ]).pipe(
            map(([cleryPersonPopoutFields, cleryPersonPopoutCustomFields]) => ({ cleryPersonPopoutFields, cleryPersonPopoutCustomFields }))
        );
    }

    public getIncidentCleryPersons(incidentId: number): Observable<CleryPersonModel[]> {
        return this.httpClient.get<CleryPersonModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/persons/incidents/${incidentId}`)
        ).pipe(
            tap((persons: CleryPersonModel[]) => {
                this.incidentCleryPersonsSourceSignal.set(persons);
            }),
            catchError(() => {
                this.incidentCleryPersonsSourceSignal.set([]);
                return of([]);
            })
        );
    }

    public createIncidentCleryPerson(person: CleryPersonModel) {
        return this.httpClient
            .post<CleryPersonModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/persons`), person
            ).pipe(
                tap((response: CleryPersonModel) => {
                    this.incidentCleryPersonsSourceSignal.update(persons => [...persons, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.CleryPersonType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentCleryPerson(person: CleryPersonModel) {
        return this.httpClient
            .put<CleryPersonModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/persons/${person.id}`
                ),
                person
            ).pipe(
                tap((response: CleryPersonModel) => {
                    this.incidentCleryPersonsSourceSignal.update(persons => persons.map(p => p.id === response.id ? response : p));
                }),
            );
    }

    public deleteIncidentCleryPerson(cleryPersonId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/persons/${cleryPersonId}`),
        ).pipe(
            tap(() => {
                this.incidentCleryPersonsSourceSignal.update(persons => persons.filter(person => person.id !== cleryPersonId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.CleryPersonType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setIncidentActiveCleryLocation(location: CleryLocationModel | null): void {
        this.incidentActiveCleryLocationSourceSignal.set(location);
    }

    public setCleryLocationPopoutForm() {
        return forkJoin([
            of(cleryLocationPopoutFormFields.fields),
            of(cleryLocationPopoutFormFields.customFields)
        ]).pipe(
            map(([cleryLocationPopoutFields, cleryLocationPopoutCustomFields]) => ({ cleryLocationPopoutFields, cleryLocationPopoutCustomFields }))
        );
    }

    public getIncidentCleryLocations(incidentId: number): Observable<CleryLocationModel[]> {
        return this.httpClient.get<CleryLocationModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/locations/incidents/${incidentId}`)
        ).pipe(
            tap((locations: CleryLocationModel[]) => {
                this.incidentCleryLocationsSourceSignal.set(locations);
            }),
            catchError(() => {
                this.incidentCleryLocationsSourceSignal.set([]);
                return of([]);
            })
        );
    }

    public createIncidentCleryLocation(location: CleryLocationModel) {
        return this.httpClient
            .post<CleryLocationModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/locations`), location
            ).pipe(
                tap((response: CleryLocationModel) => {
                    this.incidentCleryLocationsSourceSignal.update(locations => [...locations, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.CleryLocationType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentCleryLocation(location: CleryLocationModel) {
        return this.httpClient
            .put<CleryLocationModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/locations/${location.id}`
                ),
                location
            ).pipe(
                tap((response: CleryLocationModel) => {
                    this.incidentCleryLocationsSourceSignal.update(locations => locations.map(l => l.id === response.id ? response : l));
                }),
            );
    }

    public deleteIncidentCleryLocation(cleryLocationId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/Locations/${cleryLocationId}`),
        ).pipe(
            tap(() => {
                this.incidentCleryLocationsSourceSignal.update(locations => locations.filter(location => location.id !== cleryLocationId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.CleryLocationType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    public setIncidentActiveCleryArrest(Arrest: CleryArrestModel | null): void {
        this.incidentActiveCleryArrestSourceSignal.set(Arrest);
    }

    public setCleryArrestPopoutForm() {
        return forkJoin([
            of(cleryArrestPopoutFormFields.fields),
            of(cleryArrestPopoutFormFields.customFields)
        ]).pipe(
            map(([cleryArrestPopoutFields, cleryArrestPopoutCustomFields]) => ({ cleryArrestPopoutFields, cleryArrestPopoutCustomFields }))
        );
    }

    public getIncidentCleryArrests(incidentId: number): Observable<CleryArrestModel[]> {
        return this.httpClient.get<CleryArrestModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/arrests/incidents/${incidentId}`)
        ).pipe(
            tap((Arrests: CleryArrestModel[]) => {
                this.incidentCleryArrestsSourceSignal.set(Arrests);
            }),
            catchError(() => {
                this.incidentCleryArrestsSourceSignal.set([]);
                return of([]);
            })
        );
    }

    public createIncidentCleryArrest(Arrest: CleryArrestModel) {
        return this.httpClient
            .post<CleryArrestModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/arrests`), Arrest
            ).pipe(
                tap((response: CleryArrestModel) => {
                    this.incidentCleryArrestsSourceSignal.update(Arrests => [...Arrests, response]);
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.CleryArrestType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentCleryArrest(Arrest: CleryArrestModel) {
        return this.httpClient
            .put<CleryArrestModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/clery/arrests/${Arrest.id}`
                ),
                Arrest
            ).pipe(
                tap((response: CleryArrestModel) => {
                    this.incidentCleryArrestsSourceSignal.update(Arrests => Arrests.map(p => p.id === response.id ? response : p));
                }),
            );
    }

    public deleteIncidentCleryArrest(cleryArrestId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/clery/arrests/${cleryArrestId}`),
        ).pipe(
            tap(() => {
                this.incidentCleryArrestsSourceSignal.update(Arrests => Arrests.filter(Arrest => Arrest.id !== cleryArrestId));
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.CleryArrestType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }
    //#endregion

    //#region restrictions
    public restrictIncidentAccess(incidentReportAccess: IncidentReportAccessModel) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportAccess.incidentReportId}/restrict`),
            incidentReportAccess
        );
    }

    public getIncidentRestrictionAccess(incidentReportId: number) {
        return this.httpClient.get<IncidentReportAccessModel>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportId}/access`), {}
        );
    }

    public removeIncidentRestriction(incidentReportId: number) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportId}/remove-restriction`), {}
        );
    }

    public removeAgencyAndCobraIncidentRestriction(incidentReportId: number) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportId}/remove-agency-cobra-restrictions`), {}
        );
    }


    public editCobraIncidentRestriction(incidentReportId: number, isCobraRestricted: boolean) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportId}/cobra-restriction`),
            isCobraRestricted
        );
    }

    public updateIncidentAccess(incidentReportAccess: IncidentReportAccessUpdateModel) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentReportAccess.incidentReportId}/update-users-and-groups-incident-report-access`),
            incidentReportAccess
        );
    }

    public updateNarrativeAccess(narrativeAccess: NarrativeAccessModel) {
        return this.httpClient.put(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${narrativeAccess.narrativeId}/update-users-and-groups-narrative-access`),
            narrativeAccess
        );
    }
    //#endregion

    //#region Traffic Stop
    public getTrafficStopId(incidentReportId: number) {
        return this.httpClient
            .get<number>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/traffic-stop/incidents/${incidentReportId}`
                )
            );
    }

    public setTrafficStopPrimaryInformationForm() {
        return forkJoin([
            of(trafficStopPrimaryInfoFormFields.fields),
            of(trafficStopPrimaryInfoFormFields.customFields)
        ]).pipe(
            map(([trafficStopPrimaryInfoForm, trafficStopPrimaryInfoCustomFields]) => ({ trafficStopPrimaryInfoForm, trafficStopPrimaryInfoCustomFields }))
        );
    }

    public getIncidentTrafficStopPrimaryInfo(incidentReportId: number) {
        return this.httpClient
            .get<TrafficStopPrimaryInfoModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/traffic-stop/primary-info/incidents/${incidentReportId}`
                )
            );
    }

    public updateIncidentTrafficStopPrimaryInfo(primaryInfo: TrafficStopPrimaryInfoModel) {
        return this.httpClient
            .put<TrafficStopPrimaryInfoModel>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/traffic-stop/primary-info`
                ),
                primaryInfo
            )
    }

    public setIncidentActiveTrafficStopPerson(person: number | null): void {
        this.incidentActiveTrafficStopPersonSourceSignal.set(person);
    }

    public setTrafficStopPersonPopoutForm() {
        return forkJoin([
            of(trafficStopPersonPopoutFormFields.fields),
            of(trafficStopPersonPopoutFormFields.customFields)
        ]).pipe(
            map(([trafficStopPersonPopoutFields, trafficStopPersonPopoutCustomFields]) => ({ trafficStopPersonPopoutFields, trafficStopPersonPopoutCustomFields }))
        );
    }

    public getIncidentTrafficStopPerson(trafficStopId: number): Observable<PersonIncidentModel[]> {
        return this.httpClient.get<PersonIncidentModel | null>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/traffic-stop/${trafficStopId}/person`)
        )
            .pipe(
                tap((person) => {
                    if (!person) {
                        this.incidentTrafficStopPersonsSourceSignal.set([]);
                        this.incidentActiveTrafficStopPersonSourceSignal.set(null);
                    }
                    else {
                        this.incidentTrafficStopPersonsSourceSignal.set([person]);
                        this.incidentActiveTrafficStopPersonSourceSignal.set(person.id!);
                    }
                }),
                map((person) => {
                    if (!person)
                        return [];
                    else
                        return [person];
                }),
                catchError(() => {
                    this.incidentTrafficStopPersonsSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    public updateIncidentTrafficStopPerson(trafficStopId: number, personId: number) {
        return this.httpClient.put<PersonIncidentModel | null>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/traffic-stop/${trafficStopId}/person`
                ),
                personId
            )
            .pipe(
                tap((person) => {
                    if (!this.incidentTrafficStopPersonsComputedSignal().length && !!person)
                        this.assocationCountsSourceSignal.update(counts => counts.map(c => 
                            c.associationCountType == CountType.TrafficStopPersonType ? { ...c, count: c.count + 1 } : c
                    ));
                    if (!person) {
                        this.incidentTrafficStopPersonsSourceSignal.set([]);
                        this.incidentActiveTrafficStopPersonSourceSignal.set(null);
                    }
                    else {
                        this.incidentTrafficStopPersonsSourceSignal.set([person]);
                        this.incidentActiveTrafficStopPersonSourceSignal.set(person.id!);
                    }
                }),
            );
    }

    public deleteIncidentTrafficStopPerson(trafficStopId: number): Observable<any> {
        return this.httpClient.delete(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/traffic-stop/${trafficStopId}/person`),
        ).pipe(
            tap(() => {
                this.incidentTrafficStopPersonsSourceSignal.set([]);
                this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                    c.associationCountType === CountType.TrafficStopPersonType ? { ...c, count: c.count - 1 } : c
                ));
            })
        );
    }

    //#endregion

    getAssociationCounts(incidentReportId: number): Observable<AssociationCountModel[]> {
        return this.httpClient.get<AssociationCountModel[]>(`${this.environmentService.pathfinderApiUrl}/api/v1/incidents/${incidentReportId}/associations-count`)
            .pipe(
                tap((associationCounts: AssociationCountModel[]) => {
                    this.assocationCountsSourceSignal.set(associationCounts);
                }),
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred retrieving assocation counts for incident report with id ${incidentReportId}.`);
                    this.assocationCountsSourceSignal.set([]);
                    return EMPTY;
                }));
    }

    triggerUpdateEvent(): void {
        this.updateEvent.next({});
    }

    getUpdateEvent(): Observable<any> {
        return this.updateEvent.asObservable();
    }

    public clearIncidentReportTypesSignal() {
        this.incidentReportTypesSourceSignal.set([]);
    }

    public isApprovedIncident = computed(() => {
        return this.incidentReportReviewComputedSignal()?.statusId === IncidentReviewStatus.Approved
    });

    public updateStatutoryViolationCounts(action?: 'increment' | 'decrement'): void {
        this.assocationCountsSourceSignal.update(counts =>
            counts.map(c =>
                c.associationCountType === CountType.StatutoryViolationType ? { ...c, count: c.count + (action === 'increment' ? 1 : -1) } : c));
    }

    public getCadHistory(incidentReportId: number, unitIds: number[]) {
        let params = new HttpParams();

        if (unitIds.length > 0) {
            unitIds.forEach(unit => {
                params = params.append('unitIds', unit.toString());
            });
        }

        return this.httpClient.get<CadEventHistoryModel[]>(
            this.environmentService.pathfinderApiUrl
                .concat(`/api/v1/incidents/${incidentReportId}/cad-history`),
            { params }
        );
    }


    getChargingDocumentIncidents(incidentId: number): Observable<ChargingDocumentIncidentReportModel[]> {
        return this.httpClient.get<ChargingDocumentIncidentReportModel[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v1/association/charging-document-incident-report?incidentReportId=${incidentId}`))
            .pipe(
                catchError(() => {
                    this.toastMessageService.showError(`An error occurred retrieving Charging Document Incident Report associations.`);
                    return EMPTY;
                }),
                tap((data) => {
                    this.chargingDocumentsSourceSignal.set(data);
                })
            );
    }

    //#region history

    public getPrimaryInformationHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentReportPrimaryInfoSectionHistoryModel>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-report-primary-info-section-history`)
            );
    }

    public getDateHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentReportDateHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-report-date-history`)
            );
    }

    public getLocationHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentReportAddressHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-report-address-history`)
            );
    }

    public getPersonIncidentHistory(incidentId: number) {
        return this.httpClient
            .get<PersonAndIncidentReportHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/person-incident-report-history`)
            );
    }

    public getOffenseHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentStatutoryViolationHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-statutory-violation-history`)
            );
    }

    public getVehicleHistory(incidentId: number) {
        return this.httpClient
            .get<VehicleDetailAndIncidentReportHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/vehicle-incident-report-history`)
            );
    }

    public getUnitHistory(incidentId: number) {
        return this.httpClient
            .get<UnitOfficerHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-unit-officer-history`)
            );
    }

    public getOrganizationIncidentHistory(incidentId: number) {
        return this.httpClient
            .get<OrganizationAndIncidentReportHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/organization-incident-report-history`)
            );
    }

    public getPropertyIncidentHistory(incidentId: number) {
        return this.httpClient
            .get<PropertyAndIncidentReportHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/property-incident-report-history`)
            );
    }

    public getIncidentIncidentHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentReportAndIncidentReportHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-incident-report-history`)
            );
    }

    public getIncidentPersonVehicleHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentAssociateToVehiclesSectionHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-person-vehicle-section-history`)
            );
    }

    public getIncidentNarrativeHistory(incidentId: number) {
        return this.httpClient
            .get<IncidentNarrativeSectionHistoryModel[]>(
                this.environmentService.pathfinderApiUrl.concat(`/api/v1/incidents/${incidentId}/incident-narrative-section-history`)
            );
    }
    //#endgion


    public getAlarmResponses(incidentId: number): Observable<AlarmResponseModel[]> {
        return this.httpClient
            .get<AlarmResponseModel[]>(
                this.environmentService.pathfinderApiUrl.concat(
                    `/api/v1/association/incident-report-alarm-responses?incidentReportId=${incidentId}`
                )
            )
            .pipe(
                tap((response) => {
                    this.alarmResponsesSourceSignal.set(response);
                }),
                catchError(() => {
                    this.alarmResponsesSourceSignal.set([]);
                    return of([]);
                })
            );
    }

    //#region Incident Report and Supporting Deposition
    public getIncidentReportAndSupportingDepositionByIncidentId(incidentId: number): Observable<IncidentReportAndSupportingDepositionModel[]> {
        return this.httpClient
            .get<IncidentReportAndSupportingDepositionModel[]>(
                this.environmentService.digitalMediaApiUrl.concat(`/api/v1/incident-reports/${incidentId}/supporting-depositions`)
            )
            .pipe(
                switchMap((supportingDepositions) => {
                    if (!supportingDepositions || supportingDepositions.length === 0) {
                        return of([]); // normalize to empty array
                    }

                    const uniqueUserIds = Array.from(
                        new Set(
                            supportingDepositions
                                .flatMap(dep => {
                                    const s = dep.supportingDeposition;
                                    return [
                                        // root IDs only used for date normalization (no names added at root)
                                        dep.createdByUserId,
                                        dep.updatedByUserId,
                                        s?.createdByUserId,
                                        s?.updatedByUserId,
                                        s?.textUpdatedById,
                                    ];
                                })
                                .filter((id): id is number => typeof id === 'number')
                        )
                    );

                    if (uniqueUserIds.length === 0) {
                        return of(
                            supportingDepositions.map(dep => this.normalizeNestedOnly(dep, {}))
                        );
                    }

                    return this.authService.getUsersFirstAndLastNamesByIds(uniqueUserIds).pipe(
                        map((userNames: Record<number, string>) =>
                            supportingDepositions.map(dep => this.normalizeNestedOnly(dep, userNames))
                        )
                    );
                }),
                tap((resp) => {
                    this.incidentReportSupportingDepositionSourceSignal.set(resp);
                })
            );
    }


    normalizeNestedOnly(
        dep: IncidentReportAndSupportingDepositionModel,
        userNames: Record<number, string>
    ): IncidentReportAndSupportingDepositionModel {
        const toDate = (d: unknown): Date | undefined => (d ? new Date(d as any) : undefined);
        const s = dep.supportingDeposition;

        return {
            ...dep,
            createdOn: toDate(dep.createdOn),
            updatedOn: toDate(dep.updatedOn),
            supportingDeposition: s
                ? {
                    ...s,
                    createdOn: toDate(s.createdOn),
                    updatedOn: toDate(s.updatedOn),
                    textUpdatedOn: toDate(s.textUpdatedOn),

                    createdByUserName:
                        s.createdByUserId != null ? userNames[s.createdByUserId] : s.createdByUserName,
                    updatedByUserName:
                        s.updatedByUserId != null ? userNames[s.updatedByUserId] : s.updatedByUserName,
                    textUpdatedByUserName:
                        s.textUpdatedById != null ? userNames[s.textUpdatedById] : s.textUpdatedByUserName,
                }
                : undefined,
        };
    }

    public createIncidentReportAndSupportingDeposition(incidentId: number, incidentReportAndSupportingDeposition: IncidentReportAndSupportingDepositionModel): Observable<IncidentReportAndSupportingDepositionModel> {
        return this.httpClient.post<IncidentReportAndSupportingDepositionModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/incident-reports/${incidentId}/supporting-depositions`), incidentReportAndSupportingDeposition
        )
            .pipe(
                tap((item: IncidentReportAndSupportingDepositionModel) => {
                    this.incidentReportSupportingDepositionSourceSignal.update(items => {
                        return [...items, item];
                    });
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndSupportingDepositionType ? { ...c, count: c.count + 1 } : c
                    ));
                })
            );
    }

    public updateIncidentReportAndSupportingDeposition(incidentId: number, incidentReportAndSupportingDeposition: IncidentReportAndSupportingDepositionModel): Observable<IncidentReportAndSupportingDepositionModel> {
        return this.httpClient.put<IncidentReportAndSupportingDepositionModel>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/incident-reports/${incidentId}/supporting-depositions`), incidentReportAndSupportingDeposition
        )
            .pipe(
                tap((response: IncidentReportAndSupportingDepositionModel) => {
                    this.incidentReportSupportingDepositionSourceSignal.update(items => items.map(item => item.id === response.id ? response : item));
                }),
            );
    }

    public deleteIncidentReportAndSupportingDeposition(incidentId: number, incidentReportAndSupportingDeposition: IncidentReportAndSupportingDepositionModel) {
        return this.httpClient.delete(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/incident-reports/${incidentId}/supporting-depositions`), { body: incidentReportAndSupportingDeposition }
        )
            .pipe(
                tap(() => {
                    this.incidentReportSupportingDepositionSourceSignal.update(_ => this.incidentReportSupportingDepositionSourceSignal()?.filter(sd => sd.id !== incidentReportAndSupportingDeposition.id));
                    this.assocationCountsSourceSignal.update(counts => counts.map(c =>
                        c.associationCountType === CountType.AssociationIncidentReportAndSupportingDepositionType ? { ...c, count: c.count - 1 } : c
                    ));
                })
            );
    }

    public getIncidentReportAndSupportingDepositionContent(incidentId: number, supportingDepositionId: number): Observable<Blob> {
        return this.httpClient.get<Blob>(
            this.environmentService.digitalMediaApiUrl.concat(`/api/v1/incident-reports/${incidentId}/supporting-depositions/${supportingDepositionId}/content`),
            {
                responseType: 'blob' as 'json'
            }
        );
    }    
    //#endregion

    public getIncidentEventRecordTypes(incidentId: number): Observable<IncidentEventRecordType[]> {
        return this.httpClient.post<IncidentEventRecordType[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v2/queries/GetIncidentEventRecordTypes`), { incidentId }
        );
    }

    public updateIncidentEventRecordTypes(incidentId: number, eventRecordTypes: EventRecordType[]): Observable<IncidentEventRecordType[]> {
        return this.httpClient.post<IncidentEventRecordType[]>(
            this.environmentService.pathfinderApiUrl.concat(`/api/v2/commands/UpdateIncidentEventRecordTypes`), { incidentId, eventRecordTypeIds: eventRecordTypes }
        );
    }
}